import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  ExpandLess,
  ExpandMore,
  GraphicEq,
  Mic,
  MicOff,
  OpenInNew,
  Psychology
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import ColorModeToggle from '../components/ColorModeToggle';
import io from 'socket.io-client';
import axios from 'axios';
import AiInsightsDialog from '../components/AiInsightsDialog';
import LiveStudioWidgets from '../live/LiveStudioWidgets';
import RoomConsole from '../live/RoomConsole';
import {
  LIVE_STUDIO_WIDGET_IDS,
  loadLiveStudioSettings,
  saveLiveStudioSettings
} from '../live/liveStudioSettings';
import { useAuth } from '../contexts/AuthContext';
import {
  buildLiveTimingProjection,
  getIntroDuration,
  getPlayoutDuration,
  getTrackPlaylistIndex,
  hasAuthoritativePlayoutTiming,
  mergeLiveNowPlayingTrack
} from '../utils/livePlayoutTiming';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const normalizePlayoutStatusVersion = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed < 1e12 ? parsed * 1000 : parsed;
};

// Local-time date string (YYYY-MM-DD). Do NOT use toISOString() for this:
// it returns the UTC date, which is wrong around midnight in non-UTC zones.
const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// 'YYYY-MM-DD' -> next calendar day as 'YYYY-MM-DD' (noon anchor avoids DST edges)
const addOneDay = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return formatLocalDate(d);
};

const logHeadCellSx = {
  color: 'var(--rs-muted)',
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  borderBottom: '1px solid rgba(148,163,184,0.16)',
  bgcolor: 'var(--rs-overlay-strong)',
  py: 1.35,
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const logBodyCellSx = {
  borderBottom: '1px solid rgba(148,163,184,0.08)',
  color: 'var(--rs-text)',
  py: 1.4,
};

const logActionGroupSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.25,
  px: 0.35,
  py: 0.2,
  borderRadius: 1.5,
  bgcolor: 'var(--rs-overlay)',
  border: '1px solid rgba(148,163,184,0.14)',
};

function HorizontalAudioMeter({ label, value }) {
  const numSegments = 16;
  const normalizedValue = Math.max(0, Math.min(100, value));
  const activeSegments = Math.round((normalizedValue / 100) * numSegments);
  
  const getSegmentColor = (index) => {
    // Green for first ~7 segments (0-6)
    if (index < 7) return '#4caf50';
    // Yellow for next ~5 segments (7-11)
    if (index < 12) return '#ffeb3b';
    // Orange/Red for last ~4 segments (12-15)
    if (index < 15) return '#ff9800';
    return '#f44336';
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'var(--rs-accent)', fontWeight: 500 }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 0.3,
          alignItems: 'center',
          width: '100%',
          height: 24,
          bgcolor: 'var(--rs-surface)',
          borderRadius: 0.5,
          p: 0.5,
        }}
      >
        {Array.from({ length: numSegments }, (_, index) => {
          const isActive = index < activeSegments;
          const segmentColor = getSegmentColor(index);
          
          return (
            <Box
              key={index}
              sx={{
                flex: 1,
                height: '100%',
                bgcolor: isActive ? segmentColor : 'var(--rs-meter-idle)',
                borderRadius: 0.3,
                transition: 'all 0.1s ease-out',
                boxShadow: isActive
                  ? `0 0 4px ${segmentColor}40, inset 0 0 2px ${segmentColor}60`
                  : 'none',
                border: `1px solid ${isActive ? segmentColor : 'var(--rs-border)'}`,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default function Live() {
  const { hasSystemFeature } = useAuth();
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(
    () => loadLiveStudioSettings().stationId
  );
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update time every second to keep times dynamically updating
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second for dynamic time display
    return () => clearInterval(interval);
  }, []);

  const clockDate = useMemo(() => formatLocalDate(currentTime), [currentTime]);
  const clockHour = useMemo(() => currentTime.getHours(), [currentTime]);
  const [mixerPlaylistDate, setMixerPlaylistDate] = useState(null);
  const [mixerPlaylistHour, setMixerPlaylistHour] = useState(null);
  const [playlistItems, setPlaylistItems] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');

  const [outputDevices, setOutputDevices] = useState([]);
  const [inputDevices, setInputDevices] = useState([]);
  const [selectedOutput, setSelectedOutput] = useState(
    () => loadLiveStudioSettings().devices.audioOutputId
  );
  const [selectedInput, setSelectedInput] = useState(
    () => loadLiveStudioSettings().devices.audioInputId
  );
  const [supportsSinkId, setSupportsSinkId] = useState(false);

  const [micEnabled, setMicEnabled] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [playoutLevel, setPlayoutLevel] = useState(0);
  const [currentPlayoutIndex, setCurrentPlayoutIndex] = useState(null);
  const [isOnAir, setIsOnAir] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [playStartTime, setPlayStartTime] = useState(null);
  const [webrtcConnected, setWebrtcConnected] = useState(false);
  const [webrtcError, setWebrtcError] = useState('');
  const [muteMix, setMuteMix] = useState(false);
  const [stopAfterTracks, setStopAfterTracks] = useState(new Set()); // track indices where chain stops (red)
  // Runtime mixer tracks carry the authoritative beatmix cue points and the
  // original raw-log index. Keep that data instead of retaining only metadata.
  const [nowPlayingOverride, setNowPlayingOverride] = useState(null);
  const [upNextOverride, setUpNextOverride] = useState([]);
  const [playoutProgress, setPlayoutProgress] = useState(null);

  const [cartwalls, setCartwalls] = useState([]);
  const [selectedCartwall, setSelectedCartwall] = useState(null);
  const [selectedCartwallId, setSelectedCartwallId] = useState(null);
  const [createCartwallDialogOpen, setCreateCartwallDialogOpen] = useState(false);
  const [newCartwallName, setNewCartwallName] = useState('');
  const [createCartwallError, setCreateCartwallError] = useState('');
  const [createCartwallLoading, setCreateCartwallLoading] = useState(false);
  const [cartSearch, setCartSearch] = useState('');
  const [legacyExpanded, setLegacyExpanded] = useState(false);
  const [editingLayout, setEditingLayout] = useState(false);
  const [studioLayouts, setStudioLayouts] = useState(() => loadLiveStudioSettings().layout);
  const [visibleWidgets, setVisibleWidgets] = useState(() => loadLiveStudioSettings().widgets);
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const [insertAtIndex, setInsertAtIndex] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [expandedBreaks, setExpandedBreaks] = useState(new Set());
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [aiInsightsOpen, setAiInsightsOpen] = useState(false);
  const [aiInsightsItem, setAiInsightsItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [allAssets, setAllAssets] = useState([]); // Store all assets for filtering

  const socketRef = useRef(null);
  const selectedStationRef = useRef('');
  const webrtcPeerRef = useRef(null);
  const webrtcStationRef = useRef(null);
  const webrtcOperationRef = useRef(0);
  const webrtcLocalStreamRef = useRef(null);
  const webrtcRemoteAudioRef = useRef(null);
  const micMeterRef = useRef(null);
  const playoutMeterRef = useRef(null);
  const playlistItemsRef = useRef([]);
  const playlistRequestRef = useRef(0);
  const playlistQueryKeyRef = useRef('');
  const loadedPlaylistQueryKeyRef = useRef('');
  const categoriesRequestRef = useRef(0);
  const assetsRequestRef = useRef(0);
  const lastSocketStatusRef = useRef(0);
  const latestPlayoutStatusVersionRef = useRef(null);
  // Ref (not state) so socket handlers registered on mount never see a stale value
  const serverClockOffsetMsRef = useRef(0);

  // When mixer is playing, use its reported date/hour for playlist display;
  // fall back to wall clock when not on air
  const selectedDate = useMemo(
    () => (mixerPlaylistDate && isOnAir) ? mixerPlaylistDate : clockDate,
    [mixerPlaylistDate, isOnAir, clockDate]
  );
  const selectedHour = useMemo(
    () => (mixerPlaylistHour !== null && isOnAir) ? mixerPlaylistHour : clockHour,
    [mixerPlaylistHour, isOnAir, clockHour]
  );
  playlistQueryKeyRef.current = `${selectedStation}|${selectedDate}|${selectedHour}`;

  const findTrackIndexInPlaylist = (track) => {
    const items = playlistItemsRef.current;
    if (!track || !Array.isArray(items) || items.length === 0) return null;

    const trackId = track.id != null ? String(track.id) : null;
    const trackPath = track.path || track.filepath || null;
    const trackTitle = (track.title || '').trim().toLowerCase();
    const trackArtist = (track.artist || '').trim().toLowerCase();

    let idx = -1;

    if (trackId) {
      idx = items.findIndex((item) => item.id != null && String(item.id) === trackId);
      if (idx >= 0) return idx;
    }

    if (trackPath) {
      idx = items.findIndex((item) => (item.path || item.filepath || null) === trackPath);
      if (idx >= 0) return idx;
    }

    if (trackTitle || trackArtist) {
      idx = items.findIndex((item) => {
        const itemTitle = (item.title || '').trim().toLowerCase();
        const itemArtist = (item.artist || '').trim().toLowerCase();
        return itemTitle === trackTitle && itemArtist === trackArtist;
      });
      if (idx >= 0) return idx;
    }

    return null;
  };

  const withCredentials = { withCredentials: true };

  const updateServerClockOffset = (serverTime) => {
    const parsed = Number(serverTime);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    const next = parsed - Date.now();
    // Every sample disagrees by its own transport delay (tens of ms of jitter).
    // Chasing each sample makes the on-air countdowns wobble around second
    // boundaries, so only adopt shifts larger than the jitter band.
    if (Math.abs(next - serverClockOffsetMsRef.current) > 250) {
      serverClockOffsetMsRef.current = next;
    }
  };

  const resolveTrackStartTimeMs = (value) => {
    if (value == null) return null;
    const direct = Number(value);
    if (Number.isFinite(direct) && direct > 0) {
      // Support both epoch milliseconds and epoch seconds payloads.
      return direct < 1e12 ? direct * 1000 : direct;
    }
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  };

  const calcElapsedFromStart = (trackStartTime, serverTimeOverride = null) => {
    const startedAtMs = resolveTrackStartTimeMs(trackStartTime);
    if (!Number.isFinite(startedAtMs)) return null;
    const overrideMs = resolveTrackStartTimeMs(serverTimeOverride);
    const serverNowMs = Number.isFinite(overrideMs) ? overrideMs : (Date.now() + serverClockOffsetMsRef.current);
    return Math.max(0, Math.floor((serverNowMs - startedAtMs) / 1000));
  };

  const stopMeterLoop = (meterRef) => {
    if (!meterRef.current) return;
    if (meterRef.current.rafId) cancelAnimationFrame(meterRef.current.rafId);
    meterRef.current.rafId = null;
    if (meterRef.current.audioContext) {
      meterRef.current.audioContext.close().catch(() => {});
    }
    meterRef.current = null;
  };

  const startMeterLoop = (sourceNode, meterRef, setLevel) => {
    if (!meterRef.current) return;
    if (meterRef.current.rafId) cancelAnimationFrame(meterRef.current.rafId);

    // Reuse the analyser if one is already attached to this source's context,
    // so repeated calls don't stack up extra analyser nodes and RAF loops.
    let analyser = meterRef.current.analyser;
    if (!analyser || analyser.context !== sourceNode.context) {
      analyser = sourceNode.context.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.3;
      sourceNode.connect(analyser);
      meterRef.current.analyser = analyser;
    }
    const data = new Uint8Array(analyser.fftSize);

    const tick = () => {
      if (!meterRef.current) return;
      analyser.getByteTimeDomainData(data);
      let sumSquares = 0;
      for (let i = 0; i < data.length; i += 1) {
        const sample = (data[i] - 128) / 128;
        sumSquares += sample * sample;
      }
      const rms = Math.sqrt(sumSquares / data.length);
      // Increased sensitivity - multiplier increased from 220 to 400
      setLevel(Math.min(100, rms * 400));
      meterRef.current.rafId = requestAnimationFrame(tick);
    };

    meterRef.current.rafId = requestAnimationFrame(tick);
  };

  const enumerateDevices = async () => {
    let permissionStream = null;
    try {
      permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      // Continue; labels may be unavailable until permission is granted.
    } finally {
      permissionStream?.getTracks().forEach((track) => track.stop());
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const outputs = devices.filter((d) => d.kind === 'audiooutput');
    const inputs = devices.filter((d) => d.kind === 'audioinput');
    setOutputDevices(outputs);
    setInputDevices(inputs);
    setSelectedOutput((current) =>
      current && outputs.some((device) => device.deviceId === current)
        ? current
        : (outputs[0]?.deviceId || '')
    );
    setSelectedInput((current) =>
      current && inputs.some((device) => device.deviceId === current)
        ? current
        : (inputs[0]?.deviceId || '')
    );
  };

  const fetchStations = async () => {
    const res = await axios.get(`${API_URL}/api/stations`, withCredentials);
    const list = res.data || [];
    setStations(list);
    setSelectedStation((current) =>
      list.some((station) => String(station.id) === String(current))
        ? current
        : (list[0]?.id ?? '')
    );
  };

  const fetchStreamStatus = async (stationId) => {
    if (!stationId) return;
    const res = await axios.get(`${API_URL}/api/stream/status`, {
      ...withCredentials,
      params: { stationId }
    });
    const status = res.data || {};
    // Only apply status if it's for the station we asked for (avoids stale/wrong station after switch or tar.gz deploy)
    const responseStationId = status.stationId != null ? Number(status.stationId) : null;
    if (responseStationId != null && Number(stationId) !== responseStationId) return;
    const statusVersion = normalizePlayoutStatusVersion(status.serverTime);
    if (
      statusVersion !== null
      && latestPlayoutStatusVersionRef.current !== null
      && statusVersion < latestPlayoutStatusVersionRef.current
    ) return;
    if (statusVersion !== null) latestPlayoutStatusVersionRef.current = statusVersion;
    updateServerClockOffset(status.serverTime);

    const statusTrack = status.currentTrack || status.track || null;

    // Rehydrate playout state on page load so "Now Playing" is available
    // before the next socket event. Prefer mixer-authoritative fields.
    const mixerIndex =
      typeof status.mixerPlaylistIndex === 'number'
        ? status.mixerPlaylistIndex
        : (typeof status.playlistIndex === 'number' ? status.playlistIndex : null);
    const authoritativeIndex = getTrackPlaylistIndex(statusTrack, mixerIndex);

    // Rehydrate mixer playlist date/hour so the correct playlist is displayed
    if (status.playlistDate) setMixerPlaylistDate(status.playlistDate);
    if (typeof status.playlistHour === 'number') setMixerPlaylistHour(status.playlistHour);

    if (statusTrack && (statusTrack.title || statusTrack.artist)) {
      setNowPlayingOverride({
        ...statusTrack,
        title: statusTrack.title || '',
        artist: statusTrack.artist || '',
        playlistIndex: typeof authoritativeIndex === 'number' ? authoritativeIndex : null,
        mixerPlaylistIndex: typeof mixerIndex === 'number' ? mixerIndex : null,
      });
      setPlayoutProgress((prev) =>
        prev?.playlistIndex === authoritativeIndex ? prev : null
      );
    }
    if (Array.isArray(status.upNext)) setUpNextOverride(status.upNext);

    const resolvedIndex =
      typeof authoritativeIndex === 'number'
        ? authoritativeIndex
        : findTrackIndexInPlaylist(statusTrack);

    if (status.isPlaying === true) {
      if (typeof resolvedIndex === 'number') setCurrentPlayoutIndex(resolvedIndex);
      setIsOnAir(true);

      const startTime = status.trackStartTime ?? status.mixerTrackStartTime;
      if (startTime) {
        const startedAtMs = resolveTrackStartTimeMs(startTime);
        if (Number.isFinite(startedAtMs)) {
          setPlayStartTime(new Date(startedAtMs));
          const elapsed = calcElapsedFromStart(startedAtMs, status.serverTime);
          if (typeof elapsed === 'number') setElapsedTime(elapsed);
        }
      }
    } else if (status.isPlaying === false) {
      // Don't let a stale HTTP poll override a recent socket-driven playing
      // state.  The socket is more timely; the poll can arrive during the
      // brief async gap between tracks and incorrectly clear everything.
      const socketFresh = (Date.now() - lastSocketStatusRef.current) < 8000;
      if (!socketFresh) {
        setIsOnAir(false);
        setCurrentPlayoutIndex(null);
        setPlayStartTime(null);
        setElapsedTime(0);
        setNowPlayingOverride(null);
        setUpNextOverride([]);
        setPlayoutProgress(null);
      }
    }
  };

  const fetchPlaylist = async () => {
    if (!selectedStation && selectedStation !== 0) return;
    const stationId = selectedStation;
    const date = selectedDate;
    const hour = selectedHour;
    const queryKey = `${stationId}|${date}|${hour}`;
    const requestId = ++playlistRequestRef.current;
    const isCurrentRequest = () =>
      playlistRequestRef.current === requestId
      && playlistQueryKeyRef.current === queryKey;
    
    // Fetch current hour's playlist
    const currentRes = await axios.get(
      `${API_URL}/api/playlist-generator/${stationId}/${date}/${hour}`,
      withCredentials
    );
    const currentItems = (currentRes.data?.items || []).map((item) => ({
      ...item,
      duration: Number(item.duration || 0),
      isNextHour: false
    }));
    
    // Fetch next hour's playlist and append
    const nextHour = (hour + 1) % 24;
    const nextDate = nextHour === 0 ? addOneDay(date) : date;
    
    try {
      const nextRes = await axios.get(
        `${API_URL}/api/playlist-generator/${stationId}/${nextDate}/${nextHour}`,
        withCredentials
      );
      const nextItems = (nextRes.data?.items || []).map((item) => ({
        ...item,
        duration: Number(item.duration || 0),
        isNextHour: true
      }));
      if (isCurrentRequest()) {
        loadedPlaylistQueryKeyRef.current = queryKey;
        setPlaylistItems([...currentItems, ...nextItems]);
      }
    } catch (error) {
      // If next hour doesn't exist yet, just use current hour
      if (error.response?.status === 404) {
        if (isCurrentRequest()) {
          loadedPlaylistQueryKeyRef.current = queryKey;
          setPlaylistItems(currentItems);
        }
      } else {
        console.error('Error fetching next hour playlist:', error);
        if (isCurrentRequest()) {
          loadedPlaylistQueryKeyRef.current = queryKey;
          setPlaylistItems(currentItems);
        }
      }
    }
  };

  const fetchCategories = async () => {
    const stationId = selectedStation;
    const requestId = ++categoriesRequestRef.current;
    try {
      const res = await axios.get(`${API_URL}/api/categories`, {
        ...withCredentials,
        params: (stationId || stationId === 0) ? { station_id: stationId } : {}
      });
      if (
        requestId !== categoriesRequestRef.current
        || String(selectedStationRef.current) !== String(stationId)
      ) return;
      setCategories(res.data || []);
    } catch (error) {
      if (
        requestId !== categoriesRequestRef.current
        || String(selectedStationRef.current) !== String(stationId)
      ) return;
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCartwalls = async () => {
    if (!selectedStation && selectedStation !== 0) return;
    try {
      const res = await axios.get(`${API_URL}/api/cartwalls`, {
        ...withCredentials,
        params: { station_id: selectedStation }
      });
      const list = res.data || [];
      setCartwalls(list);
      if (list.length === 0) {
        setSelectedCartwall(null);
        setSelectedCartwallId(null);
      } else {
        setSelectedCartwallId((prevId) => {
          const found = list.find((c) => String(c.id) === String(prevId));
          return (found || list[0])?.id ?? null;
        });
        setSelectedCartwall((prev) => {
          const prevId = prev?.id;
          const found = list.find((c) => String(c.id) === String(prevId));
          return found || list[0];
        });
      }
    } catch (err) {
      console.error('Failed to fetch cartwalls:', err);
      setCartwalls([]);
      setSelectedCartwall(null);
    }
  };

  const fetchAllAssetsForLibrary = async () => {
    if (!selectedStation && selectedStation !== 0) return;
    const stationId = selectedStation;
    const requestId = ++assetsRequestRef.current;
    try {
      const res = await axios.get(`${API_URL}/api/assets`, {
        ...withCredentials,
        params: { station_id: stationId, playout_only: true }
      });
      if (
        requestId !== assetsRequestRef.current
        || String(selectedStationRef.current) !== String(stationId)
      ) return;
      setAllAssets(res.data || []);
    } catch (error) {
      if (
        requestId !== assetsRequestRef.current
        || String(selectedStationRef.current) !== String(stationId)
      ) return;
      console.error('Error fetching assets:', error);
      setAllAssets([]);
    }
  };

  useEffect(() => {
    setSupportsSinkId(typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype);
    webrtcRemoteAudioRef.current = new Audio();
    webrtcRemoteAudioRef.current.autoplay = true;

    socketRef.current = io(API_URL, { withCredentials: true });
    socketRef.current.on('connect', () => {
      setWebrtcError('');
    });
    socketRef.current.on('disconnect', (reason) => {
      setWebrtcConnected(false);
      setWebrtcError(`Socket disconnected: ${reason}`);
    });
    socketRef.current.on('connect_error', (error) => {
      const msg = error?.message || 'unknown socket error';
      setWebrtcConnected(false);
      setWebrtcError(`Socket connect failed: ${msg}`);
    });
    socketRef.current.on('playout:status', (data) => {
      if (data?.regionId != null) return;
      if (data?.stationId && String(data.stationId) !== String(selectedStationRef.current)) return;
      const statusVersion = normalizePlayoutStatusVersion(data?.serverTime);
      if (
        statusVersion !== null
        && latestPlayoutStatusVersionRef.current !== null
        && statusVersion < latestPlayoutStatusVersionRef.current
      ) return;
      if (statusVersion !== null) latestPlayoutStatusVersionRef.current = statusVersion;
      lastSocketStatusRef.current = Date.now();
      updateServerClockOffset(data?.serverTime);

      // Sync mixer-reported playlist date/hour so the frontend fetches the
      // correct playlist (the one the mixer is actually playing from)
      if (data.playlistDate) setMixerPlaylistDate(data.playlistDate);
      if (typeof data.playlistHour === 'number') setMixerPlaylistHour(data.playlistHour);

      const activeTrack = data.currentTrack || data.track || null;
      const mixerIndex =
        typeof data?.mixerPlaylistIndex === 'number'
          ? data.mixerPlaylistIndex
          : (typeof data?.playlistIndex === 'number' ? data.playlistIndex : null);
      const authoritativeIndex = getTrackPlaylistIndex(activeTrack, mixerIndex);

      if (activeTrack && (activeTrack.title || activeTrack.artist)) {
        setNowPlayingOverride({
          ...activeTrack,
          title: activeTrack.title || '',
          artist: activeTrack.artist || '',
          playlistIndex: typeof authoritativeIndex === 'number' ? authoritativeIndex : null,
          mixerPlaylistIndex: typeof mixerIndex === 'number' ? mixerIndex : null,
        });
        setPlayoutProgress((prev) =>
          prev?.playlistIndex === authoritativeIndex ? prev : null
        );
      } else if (typeof authoritativeIndex === 'number') {
        setNowPlayingOverride((prev) => (prev && prev.playlistIndex === authoritativeIndex ? prev : null));
      }
      if (Array.isArray(data.upNext)) setUpNextOverride(data.upNext);
      const resolvedIndex =
        typeof authoritativeIndex === 'number'
          ? authoritativeIndex
          : findTrackIndexInPlaylist(activeTrack);

      if (typeof resolvedIndex === 'number') {
        setCurrentPlayoutIndex((prevIndex) => {
          const newIndex = resolvedIndex;
          if (newIndex !== prevIndex && data.trackStartTime) {
            const startedAtMs = resolveTrackStartTimeMs(data.trackStartTime);
            if (Number.isFinite(startedAtMs)) {
              setPlayStartTime(new Date(startedAtMs));
              const elapsed = calcElapsedFromStart(startedAtMs, data?.serverTime);
              if (typeof elapsed === 'number') setElapsedTime(elapsed);
            } else {
              setElapsedTime(0);
            }
          } else if (newIndex !== prevIndex) {
            setElapsedTime(0);
            setPlayStartTime(new Date(Date.now() + serverClockOffsetMsRef.current));
          }
          return newIndex;
        });
      }
      if (typeof data?.isPlaying === 'boolean') {
        setIsOnAir((prevIsPlaying) => {
          const isNowPlaying = data.isPlaying;
          if (isNowPlaying && !prevIsPlaying) {
            if (data.trackStartTime) {
              const startedAtMs = resolveTrackStartTimeMs(data.trackStartTime);
              if (Number.isFinite(startedAtMs)) {
                setPlayStartTime(new Date(startedAtMs));
                const elapsed = calcElapsedFromStart(startedAtMs, data?.serverTime);
                if (typeof elapsed === 'number') setElapsedTime(elapsed);
              } else {
                setElapsedTime(0);
              }
            } else {
              setPlayStartTime(new Date(Date.now() + serverClockOffsetMsRef.current));
              setElapsedTime(0);
            }
          } else if (!isNowPlaying) {
            setPlayStartTime(null);
            setElapsedTime(0);
            setNowPlayingOverride(null);
            setUpNextOverride([]);
            setPlayoutProgress(null);
            // Clear mixer-reported hour so we fall back to wall clock
            setMixerPlaylistDate(null);
            setMixerPlaylistHour(null);
          } else if (isNowPlaying && data.trackStartTime) {
            const startedAtMs = resolveTrackStartTimeMs(data.trackStartTime);
            if (Number.isFinite(startedAtMs)) {
              setPlayStartTime(new Date(startedAtMs));
            }
          }
          return isNowPlaying;
        });
      }
    });

    socketRef.current.on('playout:progress', (data) => {
      if (data?.regionId != null) return;
      if (data?.stationId && String(data.stationId) !== String(selectedStationRef.current)) return;
      // The legacy FFmpeg stream and the sample-clock mixer both publish this
      // event. Legacy payloads contain only currentTime and briefly replaced
      // the mixer's cue-aware countdown, making it jump backwards until the
      // next mixer sample arrived.
      if (!hasAuthoritativePlayoutTiming(data)) return;
      updateServerClockOffset(data?.serverTime);
      const progressTrack = data?.currentTrack || data?.track || null;
      const mixerIndex = typeof data?.mixerPlaylistIndex === 'number'
        ? data.mixerPlaylistIndex
        : null;
      const progressIndex = getTrackPlaylistIndex(
        progressTrack,
        typeof data?.playlistIndex === 'number' ? data.playlistIndex : mixerIndex
      );
      if (progressIndex === null) return;
      setPlayoutProgress({
        ...data,
        playlistIndex: progressIndex,
        mixerPlaylistIndex: mixerIndex
      });
    });

    socketRef.current.on('webrtc:ice-candidate', (data) => {
      if (webrtcPeerRef.current && data.candidate) {
        webrtcPeerRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
      }
    });

    // Daisy-chain state sync
    socketRef.current.on('playout:chainState', (data) => {
      if (data?.stationId && String(data.stationId) !== String(selectedStationRef.current)) return;
      setStopAfterTracks(new Set(data.stopAfterTracks || []));
    });

    // When the mixer swaps to a new hour's playlist, update immediately
    socketRef.current.on('playlist:hourChanged', (data) => {
      if (data?.regionId != null) return;
      if (data?.stationId && String(data.stationId) !== String(selectedStationRef.current)) return;
      if (data.playlistDate) setMixerPlaylistDate(data.playlistDate);
      if (typeof data.playlistHour === 'number') setMixerPlaylistHour(data.playlistHour);
    });

    fetchStations().catch((error) => setStatusMessage(error.message));
    enumerateDevices().catch(() => {});

    return () => {
      webrtcOperationRef.current += 1;
      if (socketRef.current) socketRef.current.close();
      if (webrtcPeerRef.current) {
        webrtcPeerRef.current.close();
        webrtcPeerRef.current = null;
      }
      if (webrtcLocalStreamRef.current) {
        webrtcLocalStreamRef.current.getTracks().forEach((track) => track.stop());
        webrtcLocalStreamRef.current = null;
      }
      if (webrtcRemoteAudioRef.current) {
        webrtcRemoteAudioRef.current.pause();
        webrtcRemoteAudioRef.current.srcObject = null;
      }
      stopMeterLoop(micMeterRef);
      stopMeterLoop(playoutMeterRef);
    };
  }, []);

  useEffect(() => {
    const previousStation = selectedStationRef.current;
    if (
      previousStation !== ''
      && String(previousStation) !== String(selectedStation)
    ) {
      latestPlayoutStatusVersionRef.current = null;
      webrtcOperationRef.current += 1;
      if (webrtcPeerRef.current || webrtcLocalStreamRef.current) {
        webrtcPeerRef.current?.close();
        webrtcPeerRef.current = null;
        webrtcLocalStreamRef.current?.getTracks().forEach((track) => track.stop());
        webrtcLocalStreamRef.current = null;
        if (webrtcRemoteAudioRef.current) {
          webrtcRemoteAudioRef.current.pause();
          webrtcRemoteAudioRef.current.srcObject = null;
        }
        stopMeterLoop(micMeterRef);
        stopMeterLoop(playoutMeterRef);
      }
      const activeStationId = webrtcStationRef.current;
      if (Number.isInteger(activeStationId)) {
        socketRef.current?.emit('webrtc:close', { stationId: activeStationId });
      }
      webrtcStationRef.current = null;
      setMicEnabled(false);
      setWebrtcConnected(false);
      setMuteMix(false);
      setMicLevel(0);
      setPlayoutLevel(0);
      setStatusMessage('WebRTC stopped because the selected station changed.');
    }
    selectedStationRef.current = selectedStation;
    playlistRequestRef.current += 1;
    loadedPlaylistQueryKeyRef.current = '';
    categoriesRequestRef.current += 1;
    assetsRequestRef.current += 1;
    setPlaylistItems([]);
    setCategories([]);
    setAllAssets([]);
    setPlayoutProgress(null);
    saveLiveStudioSettings({ stationId: selectedStation });
  }, [selectedStation]);

  useEffect(() => {
    saveLiveStudioSettings({
      devices: {
        audioInputId: selectedInput,
        audioOutputId: selectedOutput
      }
    });
  }, [selectedInput, selectedOutput]);

  useEffect(() => {
    playlistItemsRef.current = playlistItems;
  }, [playlistItems]);

  useEffect(() => {
    if (!selectedStation && selectedStation !== 0) return;
    loadedPlaylistQueryKeyRef.current = '';
    setPlaylistItems([]);
    fetchStreamStatus(selectedStation).catch(() => {});
    fetchPlaylist().catch(() => {});
    fetchCartwalls().catch(() => {});
    fetchAllAssetsForLibrary().catch(() => setAllAssets([]));
    fetchCategories().catch(() => setCategories([]));
  }, [selectedStation, selectedDate, selectedHour]);

  useEffect(() => {
    if (!selectedStation && selectedStation !== 0) return;

    const intervalId = setInterval(() => {
      fetchStreamStatus(selectedStation).catch(() => {});
    }, 3000);

    return () => clearInterval(intervalId);
  }, [selectedStation]);

  useEffect(() => {
    if (!supportsSinkId || !selectedOutput) return;
    if (webrtcRemoteAudioRef.current) {
      webrtcRemoteAudioRef.current.setSinkId(selectedOutput).catch(() => {
        setStatusMessage('Selected output device could not be applied.');
      });
    }
  }, [selectedOutput, supportsSinkId]);

  // Track elapsed time for currently playing item
  useEffect(() => {
    if (!isOnAir || currentPlayoutIndex === null || !playStartTime) {
      setElapsedTime(0);
      return;
    }

    const interval = setInterval(() => {
      const serverNowMs = Date.now() + serverClockOffsetMsRef.current;
      const elapsed = Math.max(0, Math.floor((serverNowMs - playStartTime.getTime()) / 1000));
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isOnAir, currentPlayoutIndex, playStartTime]);

  const startWebRTC = async () => {
    let localStream = null;
    let peer = null;
    let stationId = null;
    const operation = ++webrtcOperationRef.current;
    const isCurrent = () => operation === webrtcOperationRef.current;
    const disposeAttempt = () => {
      try { peer?.close(); } catch (_) {}
      localStream?.getTracks().forEach((track) => track.stop());
      if (webrtcPeerRef.current === peer) webrtcPeerRef.current = null;
      if (webrtcLocalStreamRef.current === localStream) webrtcLocalStreamRef.current = null;
    };
    try {
      if (typeof RTCPeerConnection === 'undefined') {
        throw new Error('RTCPeerConnection not available. WebRTC not supported in this browser.');
      }

      stationId = Number(selectedStation);
      if (!Number.isInteger(stationId) || stationId <= 0) {
        throw new Error('Please select a station first');
      }
      webrtcStationRef.current = stationId;

      await waitForSocketConnection();
      if (!isCurrent()) return;

      console.log('[WebRTC] Requesting microphone access...');
      const audioConstraints = selectedInput
        ? {
            deviceId: { exact: selectedInput },
            channelCount: { ideal: 1 },
            sampleRate: { ideal: 48000 },
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        : {
            channelCount: { ideal: 1 },
            sampleRate: { ideal: 48000 },
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          };
      localStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });
      if (!isCurrent()) {
        disposeAttempt();
        return;
      }
      webrtcLocalStreamRef.current = localStream;
      console.log('[WebRTC] Microphone access granted');

      console.log('[WebRTC] Creating peer connection...');
      peer = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      webrtcPeerRef.current = peer;

      localStream.getTracks().forEach((track) => {
        peer.addTrack(track, localStream);
      });

      peer.onicecandidate = (event) => {
        if (isCurrent() && event.candidate) {
          socketRef.current.emit('webrtc:ice-candidate', {
            candidate: event.candidate,
            stationId
          });
        }
      };

      peer.ontrack = async (event) => {
        if (!isCurrent()) return;
        const track = event.track;
        if (!track) {
          console.warn('[WebRTC] ontrack event has no track');
          return;
        }

        let remoteStream = null;
        if (event.streams && event.streams.length > 0 && event.streams[0] instanceof MediaStream) {
          remoteStream = event.streams[0];
        } else {
          try {
            remoteStream = new MediaStream([track]);
          } catch (err) {
            console.error('[WebRTC] Failed to create MediaStream from track:', err);
            return;
          }
        }

        if (!remoteStream || !(remoteStream instanceof MediaStream)) {
          console.error('[WebRTC] Invalid remoteStream:', remoteStream);
          return;
        }

        const tracks = remoteStream.getTracks();
        if (!tracks || tracks.length === 0) {
          console.warn('[WebRTC] remoteStream has no tracks');
          return;
        }

        if (supportsSinkId && selectedOutput) {
          // Apply the selected sound card before attaching the stream (the
          // element autoplays) so playout never leaks out of the default
          // output device.
          await webrtcRemoteAudioRef.current.setSinkId(selectedOutput).catch(() => {});
        }
        if (!isCurrent()) return;
        webrtcRemoteAudioRef.current.srcObject = remoteStream;
        let remotePlayed = false;
        try {
          await webrtcRemoteAudioRef.current.play();
          remotePlayed = true;
        } catch (err) {
          console.error('[WebRTC] Remote audio element playback failed:', err);
          setWebrtcError(`Remote audio playback failed: ${err?.message || 'unknown error'}`);
        }

        stopMeterLoop(playoutMeterRef);
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          await audioContext.resume().catch(() => {});
          const source = audioContext.createMediaStreamSource(remoteStream);
          playoutMeterRef.current = { audioContext, source };
          startMeterLoop(source, playoutMeterRef, setPlayoutLevel);

          if (!remotePlayed) {
            // Only use WebAudio output as fallback when the <audio> element
            // failed to play — otherwise the audio is heard twice (doubled).
            source.connect(audioContext.destination);
            console.warn('[WebRTC] Using WebAudio fallback output path for remote stream');
          }
        } catch (err) {
          console.error('[WebRTC] Failed to create audio context or source:', err);
        }
      };

      peer.onconnectionstatechange = () => {
        if (!isCurrent()) return;
        if (peer.connectionState === 'connected') {
          setWebrtcConnected(true);
          setWebrtcError('');
        } else if (peer.connectionState === 'failed' || peer.connectionState === 'disconnected') {
          setWebrtcConnected(false);
          setWebrtcError(`Connection ${peer.connectionState}`);
        }
      };

      const offer = await peer.createOffer();
      if (!isCurrent()) {
        disposeAttempt();
        return;
      }
      await peer.setLocalDescription(offer);
      if (!isCurrent()) {
        disposeAttempt();
        return;
      }

      console.log('[WebRTC] Sending offer to server...');
      const answerRes = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebRTC offer timeout - server did not respond'));
        }, 10000);

        socketRef.current.emit('webrtc:offer', { offer, stationId }, (response) => {
          clearTimeout(timeout);
          resolve(response);
        });
      });
      if (!isCurrent()) {
        socketRef.current?.emit('webrtc:close', { stationId });
        disposeAttempt();
        return;
      }

      console.log('[WebRTC] Server response:', answerRes);

      if (!answerRes?.answer) {
        const errorMsg = answerRes?.error || 'WebRTC offer rejected';
        console.error('[WebRTC] Offer rejected:', errorMsg);
        throw new Error(errorMsg);
      }

      console.log('[WebRTC] Setting remote description...');
      await peer.setRemoteDescription(new RTCSessionDescription(answerRes.answer));
      if (!isCurrent()) {
        socketRef.current?.emit('webrtc:close', { stationId });
        disposeAttempt();
        return;
      }

      webrtcPeerRef.current = peer;
      setMicEnabled(true);
      setMuteMix(true); // Start muted to prevent unexpected outbound audio
      setStatusMessage('');

      // Keep mic muted until operator explicitly unmutes.
      localStream.getAudioTracks().forEach((track) => {
        track.enabled = false;
      });

      stopMeterLoop(micMeterRef);
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(localStream);
      micMeterRef.current = { audioContext };
      startMeterLoop(source, micMeterRef, setMicLevel);
    } catch (error) {
      if (!isCurrent()) {
        disposeAttempt();
        return;
      }
      console.error('[WebRTC] Start failed:', error);
      setStatusMessage(`WebRTC start failed: ${error.message}`);
      setWebrtcError(error.message);
      disposeAttempt();
      webrtcStationRef.current = null;
    }
  };

  const stopWebRTC = async () => {
    webrtcOperationRef.current += 1;
    if (webrtcPeerRef.current) {
      webrtcPeerRef.current.close();
      webrtcPeerRef.current = null;
    }
    if (webrtcLocalStreamRef.current) {
      webrtcLocalStreamRef.current.getTracks().forEach((track) => track.stop());
      webrtcLocalStreamRef.current = null;
    }
    if (webrtcRemoteAudioRef.current) {
      webrtcRemoteAudioRef.current.pause();
      webrtcRemoteAudioRef.current.srcObject = null;
    }
    setMicEnabled(false);
    setWebrtcConnected(false);
    setMuteMix(false);
    setMicLevel(0);
    stopMeterLoop(micMeterRef);
    stopMeterLoop(playoutMeterRef);
    setPlayoutLevel(0);
    const stationId = webrtcStationRef.current;
    webrtcStationRef.current = null;
    if (socketRef.current && Number.isInteger(stationId)) {
      socketRef.current.emit('webrtc:close', { stationId });
    }
  };

  const toggleMuteMix = () => {
    const next = !muteMix;
    setMuteMix(next);
    if (webrtcLocalStreamRef.current) {
      webrtcLocalStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = !next; // When muteMix is true, enabled is false (muted)
      });
    }
  };

  // Save playlist to backend
  const savePlaylist = async (itemsToSave = null) => {
    // Use provided items or fall back to current playlistItems
    const items = itemsToSave || playlistItems;
    if (!selectedStation && selectedStation !== 0) return;
    const stationId = selectedStation;
    const date = selectedDate;
    const hour = selectedHour;
    const queryKey = `${stationId}|${date}|${hour}`;
    if (loadedPlaylistQueryKeyRef.current !== queryKey) {
      setStatusMessage('Playlist is still loading for this station and hour. Please try again.');
      return;
    }
    
    try {
      // Separate current hour and next hour items
      const currentHourItems = items.filter(item => !item.isNextHour);
      const nextHourItems = items.filter(item => item.isNextHour);
      
      // Save current hour
      if (currentHourItems.length > 0 || hour === currentTime.getHours()) {
        await axios.put(
          `${API_URL}/api/playlist-generator/${stationId}/${date}/${hour}`,
          { items: currentHourItems.map(({ isNextHour, ...item }) => item) },
          withCredentials
        );
      }
      
      // Save next hour if there are items
      if (nextHourItems.length > 0) {
        const nextHour = (hour + 1) % 24;
        const nextDate = nextHour === 0 ? addOneDay(date) : date;
        
        await axios.put(
          `${API_URL}/api/playlist-generator/${stationId}/${nextDate}/${nextHour}`,
          { items: nextHourItems.map(({ isNextHour, ...item }) => item) },
          withCredentials
        );
      }
    } catch (error) {
      console.error('Error saving playlist:', error);
      setStatusMessage('Failed to save playlist: ' + (error.response?.data?.message || error.message));
    }
  };

  // Recompute start times for all items; current-hour and next-hour items are
  // timed independently from their respective hour starts.
  const recalcStartTimes = (items) => {
    const hourStart = new Date(`${selectedDate}T${String(selectedHour).padStart(2, '0')}:00:00`);
    const nextHour = (selectedHour + 1) % 24;
    const nextDate = nextHour === 0 ? addOneDay(selectedDate) : selectedDate;
    const nextHourStart = new Date(`${nextDate}T${String(nextHour).padStart(2, '0')}:00:00`);

    let currentCumulative = 0;
    let nextCumulative = 0;
    return items.map((itm) => {
      let startTime;
      if (itm.isNextHour) {
        startTime = new Date(nextHourStart.getTime() + nextCumulative * 1000);
        nextCumulative += itm.duration || 0;
      } else {
        startTime = new Date(hourStart.getTime() + currentCumulative * 1000);
        currentCumulative += itm.duration || 0;
      }
      return { ...itm, startTime: startTime.toISOString() };
    });
  };

  // After a move/insert, make the moved item's hour flag match its new position
  // so the current-hour and next-hour regions stay contiguous. Without this,
  // dragging an item across the hour divider would keep it saved in its old
  // hour's playlist.
  const syncHourFlagAfterMove = (items, movedIndex) => {
    const moved = items[movedIndex];
    if (!moved) return items;
    const nextHourItemBefore = items.some((itm, i) => i < movedIndex && itm.isNextHour);
    const currentHourItemAfter = items.some((itm, i) => i > movedIndex && !itm.isNextHour);
    let shouldBeNextHour = !!moved.isNextHour;
    if (nextHourItemBefore) shouldBeNextHour = true;
    else if (currentHourItemAfter) shouldBeNextHour = false;
    if (!!moved.isNextHour === shouldBeNextHour) return items;
    const copy = [...items];
    copy[movedIndex] = { ...moved, isNextHour: shouldBeNextHour };
    return copy;
  };

  const moveItem = async (index, direction) => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= playlistItems.length) return;
    const updated = [...playlistItems];
    const [item] = updated.splice(index, 1);
    updated.splice(target, 0, item);

    const updatedWithTimes = recalcStartTimes(syncHourFlagAfterMove(updated, target));
    setPlaylistItems(updatedWithTimes);
    // Clear selection if moved item was selected
    if (selectedItemIndex === index || selectedItemIndex === target) {
      setSelectedItemIndex(null);
    } else if (selectedItemIndex !== null) {
      // Adjust selection index if needed
      if (index < selectedItemIndex && target >= selectedItemIndex) {
        setSelectedItemIndex(selectedItemIndex - 1);
      } else if (index > selectedItemIndex && target <= selectedItemIndex) {
        setSelectedItemIndex(selectedItemIndex + 1);
      }
    }
    await savePlaylist(updatedWithTimes);
  };

  const removeItem = async (index) => {
    const updated = recalcStartTimes(playlistItems.filter((_, i) => i !== index));
    setPlaylistItems(updated);
    // Clear selection if deleted item was selected, or adjust index
    if (selectedItemIndex === index) {
      setSelectedItemIndex(null);
    } else if (selectedItemIndex !== null && selectedItemIndex > index) {
      setSelectedItemIndex(selectedItemIndex - 1);
    }
    await savePlaylist(updated);
  };

  // Handle drag and drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    
    const updated = [...playlistItems];
    const [draggedItem] = updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, draggedItem);

    const updatedWithTimes = recalcStartTimes(syncHourFlagAfterMove(updated, dropIndex));
    setPlaylistItems(updatedWithTimes);
    setDraggedIndex(null);
    // Clear selection if dragged item was selected, or adjust index
    if (selectedItemIndex === draggedIndex) {
      setSelectedItemIndex(dropIndex);
    } else if (selectedItemIndex !== null) {
      // Adjust selection index based on drag direction
      if (draggedIndex < selectedItemIndex && dropIndex >= selectedItemIndex) {
        setSelectedItemIndex(selectedItemIndex - 1);
      } else if (draggedIndex > selectedItemIndex && dropIndex <= selectedItemIndex) {
        setSelectedItemIndex(selectedItemIndex + 1);
      } else if (draggedIndex < selectedItemIndex && dropIndex < selectedItemIndex) {
        // No change needed
      } else if (draggedIndex > selectedItemIndex && dropIndex > selectedItemIndex) {
        // No change needed
      }
    }
    await savePlaylist(updatedWithTimes);
  };

  // Handle click on playlist item to add library items
  const handleItemClick = (index) => {
    setInsertAtIndex(index);
    setLibraryDialogOpen(true);
  };

  // Handle add item button - add after selected item
  const handleAddItemAfterSelected = () => {
    if (selectedItemIndex === null) return;
    // Insert after the selected item (selectedItemIndex + 1)
    setInsertAtIndex(selectedItemIndex + 1);
    setLibraryDialogOpen(true);
  };

  // Add item from library at specified index
  const handleAddFromLibrary = async (asset) => {
    if (insertAtIndex === null) return;

    const newItem = {
      title: asset.title || 'Unknown',
      artist: asset.artist || 'Unknown',
      duration: asset.duration || 180,
      filepath: asset.filepath,
      type: 'asset',
      asset_id: asset.id,
      isNextHour: insertAtIndex >= playlistItems.filter(item => !item.isNextHour).length
    };

    const updated = [...playlistItems];
    updated.splice(insertAtIndex, 0, newItem);

    const updatedWithTimes = recalcStartTimes(syncHourFlagAfterMove(updated, insertAtIndex));
    setPlaylistItems(updatedWithTimes);
    setLibraryDialogOpen(false);
    setInsertAtIndex(null);
    setSelectedCategory('');
    setCartSearch('');
    // Update selection to the newly added item
    setSelectedItemIndex(insertAtIndex);
    await savePlaylist(updatedWithTimes);
  };

  const createCartwall = async () => {
    const trimmed = newCartwallName.trim();
    const hasStation = selectedStation !== '' && selectedStation != null && selectedStation !== undefined;
    if (!trimmed) {
      setCreateCartwallError('Enter a cartwall name.');
      return;
    }
    if (!hasStation) {
      setCreateCartwallError('Select a station first.');
      return;
    }
    setCreateCartwallError('');
    setCreateCartwallLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/cartwalls`,
        { name: trimmed, station_id: selectedStation },
        withCredentials
      );
      const newCartwall = res.data;
      setCartwalls((prev) => [...prev, newCartwall]);
      setSelectedCartwall(newCartwall);
      setSelectedCartwallId(newCartwall?.id ?? null);
      setCreateCartwallDialogOpen(false);
      setNewCartwallName('');
      setStatusMessage('Cartwall created.');
      // Always open the new cartwall in a new window
      openCartwallInNewWindow(newCartwall);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      const url = `${API_URL || window.location.origin}/api/cartwalls`;
      const status = err.response?.status;
      setCreateCartwallError(
        status === 404
          ? `API not found (404). Ensure the backend is running and restarted. Endpoint: ${url}`
          : `Failed: ${msg}`
      );
    } finally {
      setCreateCartwallLoading(false);
    }
  };

  const openCartwallInNewWindow = (cartwallOrId) => {
    const idToOpen = cartwallOrId?.id ?? cartwallOrId ?? selectedCartwallId;
    if (idToOpen == null || idToOpen === '' || String(idToOpen) === 'undefined') {
      setStatusMessage('Select a cartwall and station first.');
      return;
    }
    if (!selectedStation) {
      setStatusMessage('Select a station first.');
      return;
    }
    const origin = window.location.origin;
    const basePath = window.location.pathname.replace(/\/live.*$/, '') || '';
    const url = `${origin}${basePath}/cartwall/${idToOpen}?stationId=${selectedStation}`;
    window.open(url, 'cartwall', 'width=900,height=700,menubar=no,toolbar=no,location=no,status=no');
  };

  const transport = (action) => {
    const stationId = Number(selectedStation);
    if (!Number.isInteger(stationId) || stationId <= 0) {
      setStatusMessage('Select a station before using transport controls.');
      return;
    }
    if (!socketRef.current) {
      setStatusMessage('Transport unavailable');
      return;
    }
    if (!socketRef.current.connected) {
      socketRef.current.connect();
    }
    socketRef.current.emit('playout:control', {
      action,
      stationId
    });
  };

  const waitForSocketConnection = (timeoutMs = 6000) =>
    new Promise((resolve, reject) => {
      const socket = socketRef.current;
      if (!socket) {
        reject(new Error('Socket unavailable.'));
        return;
      }
      if (socket.connected) {
        resolve(socket);
        return;
      }

      let timeoutId = null;
      const onConnect = () => {
        cleanup();
        resolve(socket);
      };
      const onError = (error) => {
        cleanup();
        reject(new Error(error?.message || 'Socket connection failed.'));
      };
      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        socket.off('connect', onConnect);
        socket.off('connect_error', onError);
      };

      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Socket connect timeout.'));
      }, timeoutMs);

      socket.on('connect', onConnect);
      socket.on('connect_error', onError);
      socket.connect();
    });

  // Daisy-chain: toggle whether playback stops after a given track index
  const toggleChain = (trackIndex) => {
    if (!socketRef.current) return;
    const stationId = Number(selectedStation);
    if (!Number.isInteger(stationId) || stationId <= 0) {
      setStatusMessage('Select a station before changing the playout chain.');
      return;
    }
    const isCurrentlyStop = stopAfterTracks.has(trackIndex);
    // Optimistic UI update
    setStopAfterTracks((prev) => {
      const next = new Set(prev);
      if (isCurrentlyStop) {
        next.delete(trackIndex);
      } else {
        next.add(trackIndex);
      }
      return next;
    });
    socketRef.current.emit('playout:chain', {
      stationId,
      trackIndex: parseInt(trackIndex),
      stop: !isCurrentlyStop
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (isoOrDate) => {
    if (!isoOrDate) return '—';
    const d = new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return '—';
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  };

  // Format clock time as HH:MM:SS for live display (right to the second)
  const formatClockTime = (date) => {
    if (!date) return '--:--:--';
    const d = date instanceof Date ? date : new Date(date);
    const h = d.getHours();
    const m = d.getMinutes();
    const s = d.getSeconds();
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Get category info (name and color) for an item
  const getCategoryInfo = (item) => {
    let categoryName = 'Unknown';
    let categoryColor = '#666666';
    
    // Check if category is directly on the item
    if (item.category) {
      if (typeof item.category === 'object' && item.category !== null) {
        if (item.category.name) {
          categoryName = item.category.name;
          categoryColor = item.category.color || '#666666';
        }
      }
    }
    
    // Check if categories array exists
    if (categoryName === 'Unknown' && item.categories && Array.isArray(item.categories) && item.categories.length > 0) {
      const firstCategory = item.categories[0];
      if (firstCategory && typeof firstCategory === 'object') {
        if (firstCategory.name) {
          categoryName = firstCategory.name;
          categoryColor = firstCategory.color || '#666666';
        }
      }
    }
    
    // Special types
    if (item.type === 'commercial_break') {
      categoryName = 'Commercial';
      categoryColor = '#ff9800'; // Orange
    } else if (item.type === 'voicetrack' || item.type === 'voice') {
      categoryName = 'Voicetrack';
      categoryColor = '#9c27b0'; // Purple
    } else if (item.type === 'hard_fixed_timer') {
      categoryName = 'Hard Timer';
      categoryColor = '#f44336'; // Red
    }
    
    // Ensure it's a valid hex color
    if (!categoryColor.startsWith('#')) {
      categoryColor = `#${categoryColor}`;
    }
    
    return { categoryName, categoryColor };
  };

  // Get category color for an item (backward compatibility)
  const getCategoryColor = (item) => {
    return getCategoryInfo(item).categoryColor;
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    if (!hex || !hex.startsWith('#')) return `rgba(0, 0, 0, ${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Group commercial breaks like in PlaylistManager
  const groupCommercialBreaks = useMemo(() => {
    if (!playlistItems.length) return [];
    
    // Calculate accurate start times: use startTime from items if available, otherwise calculate from hour start
    const hourStart = new Date(`${selectedDate}T${String(selectedHour).padStart(2, '0')}:00:00`);
    let cumulativeTime = 0;
    const grouped = [];
    let i = 0;
    
    while (i < playlistItems.length) {
      if (playlistItems[i].type === 'commercial_break') {
        const breakGroup = [];
        const groupStartIndex = i;
        let groupStartTime = null;
        
        while (i < playlistItems.length && playlistItems[i].type === 'commercial_break') {
          // Use actual startTime if available, otherwise calculate from cumulative
          let itemStartTime;
          if (playlistItems[i].startTime) {
            itemStartTime = new Date(playlistItems[i].startTime);
          } else {
            // Calculate from hour start + cumulative time
            itemStartTime = new Date(hourStart.getTime() + cumulativeTime * 1000);
          }
          
          if (groupStartIndex === i) {
            groupStartTime = itemStartTime;
          }
          
          const duration = playlistItems[i].duration || 0;
          const hours = itemStartTime.getHours();
          const minutes = itemStartTime.getMinutes();
          const seconds = itemStartTime.getSeconds();
          const categoryInfo = getCategoryInfo(playlistItems[i]);
          
          breakGroup.push({
            ...playlistItems[i],
            originalIndex: i,
            suggestedPlayTime: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
            categoryColor: categoryInfo.categoryColor,
            categoryName: categoryInfo.categoryName,
            isNextHour: playlistItems[i].isNextHour || false
          });
          cumulativeTime += duration;
          i++;
        }
        
        const totalDuration = breakGroup.reduce((sum, s) => sum + (s.duration || 0), 0);
        const groupTime = groupStartTime || new Date(hourStart.getTime() + (cumulativeTime - totalDuration) * 1000);
        const groupHours = groupTime.getHours();
        const groupMinutes = groupTime.getMinutes();
        const groupSeconds = groupTime.getSeconds();
        
        grouped.push({
          type: 'commercial_break_group',
          groupStartIndex,
          items: breakGroup,
          spotCount: breakGroup.length,
          totalDuration,
          suggestedPlayTime: `${String(groupHours).padStart(2, '0')}:${String(groupMinutes).padStart(2, '0')}:${String(groupSeconds).padStart(2, '0')}`,
          categoryColor: '#ff9800',
          categoryName: 'Commercial',
          isNextHour: playlistItems[groupStartIndex]?.isNextHour || false
        });
      } else {
        // Use actual startTime if available, otherwise calculate from cumulative
        let itemStartTime;
        if (playlistItems[i].startTime) {
          itemStartTime = new Date(playlistItems[i].startTime);
        } else {
          // Calculate from hour start + cumulative time
          itemStartTime = new Date(hourStart.getTime() + cumulativeTime * 1000);
        }
        
        const duration = playlistItems[i].duration || 0;
        const hours = itemStartTime.getHours();
        const minutes = itemStartTime.getMinutes();
        const seconds = itemStartTime.getSeconds();
        const categoryInfo = getCategoryInfo(playlistItems[i]);
        
        grouped.push({
          ...playlistItems[i],
          originalIndex: i,
          intro: playlistItems[i].intro || 0,
          suggestedPlayTime: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
          categoryColor: categoryInfo.categoryColor,
          categoryName: categoryInfo.categoryName,
          isNextHour: playlistItems[i].isNextHour || false
        });
        cumulativeTime += duration;
        i++;
      }
    }
    return grouped;
  }, [playlistItems, selectedDate, selectedHour]);

  // Calculate suggested play times for playlist items (for backward compatibility)
  // Times use actual startTime from items if available, otherwise calculate from hour start
  const calculateSuggestedPlayTimes = useMemo(() => {
    if (!playlistItems.length) return [];
    
    const hourStart = new Date(`${selectedDate}T${String(selectedHour).padStart(2, '0')}:00:00`);
    let cumulativeTime = 0;
    
    return playlistItems.map((item) => {
      // Use actual startTime if available, otherwise calculate from cumulative
      let suggestedTime;
      if (item.startTime) {
        suggestedTime = new Date(item.startTime);
      } else {
        suggestedTime = new Date(hourStart.getTime() + cumulativeTime * 1000);
      }
      
      const duration = item.duration || 0;
      cumulativeTime += duration;
      
      const hours = suggestedTime.getHours();
      const minutes = suggestedTime.getMinutes();
      const seconds = suggestedTime.getSeconds();
      
      return {
        ...item,
        intro: item.intro || 0,
        suggestedPlayTime: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
        suggestedPlayTimeShort: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
        categoryColor: getCategoryColor(item)
      };
    });
  }, [playlistItems, selectedDate, selectedHour]);

  const liveProgressTiming = useMemo(() => {
    if (!playoutProgress || playoutProgress.playlistIndex !== currentPlayoutIndex) return null;
    const sampledAtValue = Number(playoutProgress.serverTime);
    const sampledAtMs = Number.isFinite(sampledAtValue)
      ? (sampledAtValue < 1e12 ? sampledAtValue * 1000 : sampledAtValue)
      : null;
    // The 1s clock tick only re-runs this memo between socket samples; the
    // aging math must use the real clock. Aging fresh samples against the
    // stale tick made the countdown dip early and bounce back every second.
    const serverNowMs = Math.max(currentTime.getTime(), Date.now())
      + serverClockOffsetMsRef.current;
    const sampleAgeSec = Number.isFinite(sampledAtMs)
      ? Math.max(0, (serverNowMs - sampledAtMs) / 1000)
      : 0;
    const countdown = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.max(0, parsed - sampleAgeSec) : null;
    };
    const effectiveDurationSec = Number(playoutProgress.effectiveDurationSec);
    const effectiveIntroDurationSec = Number(playoutProgress.effectiveIntroDurationSec);
    return {
      observedAtMs: serverNowMs,
      transitionRemainingSec: countdown(playoutProgress.transitionRemainingSec),
      introRemainingSec: countdown(playoutProgress.introRemainingSec),
      effectiveDurationSec: Number.isFinite(effectiveDurationSec) ? effectiveDurationSec : null,
      effectiveIntroDurationSec: Number.isFinite(effectiveIntroDurationSec)
        ? effectiveIntroDurationSec
        : null
    };
  }, [currentPlayoutIndex, currentTime, playoutProgress]);

  const liveTimingProjection = useMemo(() => {
    if (!isOnAir || currentPlayoutIndex === null || !playStartTime) return new Map();
    const playlistCurrent = calculateSuggestedPlayTimes[currentPlayoutIndex] || null;
    const runtimeCurrent = nowPlayingOverride?.playlistIndex === currentPlayoutIndex
      ? { ...playlistCurrent, ...nowPlayingOverride }
      : playlistCurrent;
    return buildLiveTimingProjection({
      currentIndex: currentPlayoutIndex,
      currentTrack: runtimeCurrent,
      upNext: upNextOverride,
      startedAt: playStartTime,
      currentRemainingSec: liveProgressTiming?.transitionRemainingSec,
      observedAt: liveProgressTiming?.observedAtMs
    });
  }, [
    calculateSuggestedPlayTimes,
    currentPlayoutIndex,
    isOnAir,
    liveProgressTiming,
    nowPlayingOverride,
    playStartTime,
    upNextOverride
  ]);

  // First playlist index shown in the upcoming-items table. Everything from
  // the current item through the "After Next" preview entry is rendered above
  // the table, and a preview entry can be a commercial break group spanning
  // several playlist indices — so the cutoff must be derived from the grouped
  // entries, not from currentPlayoutIndex + a fixed offset.
  const firstTableIndex = useMemo(() => {
    if (currentPlayoutIndex === null) return 0;
    const endOfEntry = (entry) =>
      entry.type === 'commercial_break_group'
        ? entry.groupStartIndex + entry.items.length
        : entry.originalIndex + 1;

    let currentGroupIndex = -1;
    for (let i = 0; i < groupCommercialBreaks.length; i++) {
      const entry = groupCommercialBreaks[i];
      const matches = entry.type === 'commercial_break_group'
        ? entry.items.some((item) => item.originalIndex === currentPlayoutIndex)
        : entry.originalIndex === currentPlayoutIndex;
      if (matches) {
        currentGroupIndex = i;
        break;
      }
    }
    if (currentGroupIndex === -1) return currentPlayoutIndex + 1;

    const lastPreviewEntry =
      groupCommercialBreaks[currentGroupIndex + 2] ||
      groupCommercialBreaks[currentGroupIndex + 1] ||
      groupCommercialBreaks[currentGroupIndex];
    return endOfEntry(lastPreviewEntry);
  }, [groupCommercialBreaks, currentPlayoutIndex]);

  const toggleBreakExpanded = (groupStartIndex) => {
    setExpandedBreaks(prev => {
      const next = new Set(prev);
      if (next.has(groupStartIndex)) {
        next.delete(groupStartIndex);
      } else {
        next.add(groupStartIndex);
      }
      return next;
    });
  };

  const handleStudioLayoutChange = (_current, allLayouts) => {
    const next = {
      lg: Array.isArray(allLayouts?.lg) ? allLayouts.lg : studioLayouts.lg,
      xs: Array.isArray(allLayouts?.xs) ? allLayouts.xs : studioLayouts.xs,
    };
    setStudioLayouts(next);
    saveLiveStudioSettings({ layout: next });
  };

  const removeStudioWidget = (widgetId) => {
    if (widgetId !== 'side') return;
    const next = visibleWidgets.filter((id) => id !== widgetId);
    setVisibleWidgets(next);
    saveLiveStudioSettings({ widgets: next });
  };

  const restoreSideWidget = () => {
    if (visibleWidgets.includes('side')) return;
    const next = [...visibleWidgets, 'side'];
    setVisibleWidgets(next);
    saveLiveStudioSettings({ widgets: next });
  };

  return (
    <Box
      sx={{
        minHeight: 'var(--rs-vh)',
        p: { xs: 1.5, md: 2 },
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <PageHeader
          overline="Broadcast"
          title="Live Studio"
          subtitle="Go live, manage your running order and talk over the mix"
          icon={<GraphicEq />}
          actions={<ColorModeToggle placement="bottom" />}
        />

        {statusMessage && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {statusMessage}
          </Alert>
        )}

        {webrtcError && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            WebRTC: {webrtcError}
          </Alert>
        )}

        {stations.length > 1 && (
          <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)', mb: 1.5 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Station</InputLabel>
                <Select
                  value={selectedStation}
                  label="Station"
                  onChange={(e) => setSelectedStation(e.target.value)}
                >
                  {stations.map((station) => (
                    <MenuItem key={station.id} value={station.id}>
                      {station.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        )}

        {editingLayout && !visibleWidgets.includes('side') && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            action={
              <Button color="inherit" size="small" onClick={restoreSideWidget}>
                Show cartwall
              </Button>
            }
          >
            Cartwall &amp; meters widget is hidden.
          </Alert>
        )}
      </Box>

      <RoomConsole
        stationId={selectedStation}
        inputDevices={inputDevices}
        outputDevices={outputDevices}
        audioInputId={selectedInput}
        audioOutputId={selectedOutput}
        onAudioInputChange={setSelectedInput}
        onAudioOutputChange={setSelectedOutput}
        editingLayout={editingLayout}
        onEditingLayoutChange={setEditingLayout}
        onResetLayout={(defaults) => {
          setStudioLayouts(defaults.layout);
          setVisibleWidgets(defaults.widgets || [...LIVE_STUDIO_WIDGET_IDS]);
          setEditingLayout(false);
        }}
      >
        {({ participantsPanel, mixerPanel }) => (
          <LiveStudioWidgets
            layouts={studioLayouts}
            visibleWidgetIds={visibleWidgets}
            editing={editingLayout}
            onLayoutChange={handleStudioLayoutChange}
            onRemoveWidget={removeStudioWidget}
            renderWidget={(widgetId) => {
              if (widgetId === 'participants') return participantsPanel;
              if (widgetId === 'mixer') return mixerPanel;
              if (widgetId === 'side') {
                return (
                  <Box
                    className="live-studio-no-drag"
                    sx={{
                      height: '100%',
                      overflow: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" sx={{ color: 'var(--rs-accent)', mb: 1 }}>Audio Levels</Typography>
            <HorizontalAudioMeter label="Mic" value={micLevel} />
            <HorizontalAudioMeter label="Playout" value={playoutLevel} />
            <Typography variant="caption" color="text.secondary">
              LiveKit return levels are on the Server mix strips. These meters update when legacy contribution is running.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" sx={{ color: 'var(--rs-accent)', mb: 1 }}>Cartwall</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Cartwall</InputLabel>
                <Select
                  value={selectedCartwallId ?? ''}
                  label="Cartwall"
                  onChange={(e) => {
                    const val = e.target.value;
                    const id = val === '' ? null : val;
                    setSelectedCartwallId(id);
                    setSelectedCartwall(id == null ? null : cartwalls.find((c) => String(c.id) === String(id)) || null);
                  }}
                  sx={{ color: 'var(--rs-text)' }}
                >
                  <MenuItem value="">— None —</MenuItem>
                  {cartwalls.map((cw) => (
                    <MenuItem key={cw.id} value={cw.id}>{cw.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setCreateCartwallDialogOpen(true)}
                  sx={{ borderColor: 'var(--rs-accent)', color: 'var(--rs-accent)' }}
                >
                  New
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<OpenInNew />}
                  disabled={!selectedCartwallId}
                  onClick={() => openCartwallInNewWindow()}
                  sx={{ bgcolor: '#00695c', color: '#fff', '&:hover': { bgcolor: '#00897b' } }}
                >
                  Open
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1.25 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                mb: legacyExpanded ? 1 : 0,
              }}
              onClick={() => setLegacyExpanded((value) => !value)}
            >
              <Typography variant="subtitle2" sx={{ color: 'var(--rs-muted)' }}>Legacy contribution</Typography>
              <IconButton
                size="small"
                sx={{ color: 'var(--rs-muted)' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setLegacyExpanded((value) => !value);
                }}
              >
                {legacyExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={legacyExpanded}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Prefer Live Studio Mic / Monitor above. This path is for desk-routed WebRTC contribution only.
              </Alert>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Output</InputLabel>
                    <Select value={selectedOutput} label="Output" onChange={(e) => setSelectedOutput(e.target.value)}>
                      {outputDevices.map((d) => (
                        <MenuItem key={d.deviceId} value={d.deviceId}>{d.label || `Output ${d.deviceId.slice(0, 8)}`}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Input</InputLabel>
                    <Select value={selectedInput} label="Input" onChange={(e) => setSelectedInput(e.target.value)}>
                      {inputDevices.map((d) => (
                        <MenuItem key={d.deviceId} value={d.deviceId}>{d.label || `Input ${d.deviceId.slice(0, 8)}`}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {micEnabled ? (
                      <>
                        <Button
                          variant={muteMix ? 'contained' : 'outlined'}
                          color={muteMix ? 'warning' : 'success'}
                          size="small"
                          startIcon={muteMix ? <MicOff /> : <Mic />}
                          onClick={toggleMuteMix}
                        >
                          {muteMix ? 'Unmute' : 'Live'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Mic />}
                          onClick={stopWebRTC}
                        >
                          Stop
                          {webrtcConnected && <Chip label="On" size="small" color="success" sx={{ ml: 0.5 }} />}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        startIcon={<Mic />}
                        onClick={startWebRTC}
                      >
                        Start WebRTC
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
              {!supportsSinkId && (
                <Alert severity="warning" sx={{ mt: 1 }}>Output device selection not supported.</Alert>
              )}
            </Collapse>
          </CardContent>
        </Card>
                  </Box>
                );
              }
              return (
                <Card
                  className="live-studio-no-drag"
                  sx={{
                    bgcolor: 'var(--rs-paper)',
                    border: '1px solid var(--rs-border)',
                    height: '100%',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ mb: 1.25, flexShrink: 0 }}>
                      <Typography variant="subtitle1" sx={{ color: 'var(--rs-accent)', fontWeight: 700 }}>
                        Playout Log
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Running order for this hour — drag rows to reorder
                      </Typography>
                    </Box>

              {/* Currently Playing Item - Big Display */}
              {(() => {
                const hasPlaylistItem = currentPlayoutIndex !== null
                  && currentPlayoutIndex >= 0
                  && currentPlayoutIndex < calculateSuggestedPlayTimes.length;
                if (hasPlaylistItem || nowPlayingOverride) {
                const currentItem = hasPlaylistItem
                  ? calculateSuggestedPlayTimes[currentPlayoutIndex]
                  : null;
                // Runtime metadata describes the audio engine's actual output.
                // Keep it authoritative even when a regenerated/grouped log has
                // a different row index.
                const timingItem = mergeLiveNowPlayingTrack(currentItem, nowPlayingOverride);
                if (!timingItem) return null;
                const displayTitle = timingItem.title || 'Untitled';
                const displayArtist = timingItem.artist || '-';
                const markerIntro = getIntroDuration(timingItem);
                const intro = liveProgressTiming?.effectiveIntroDurationSec ?? markerIntro;
                const currentMixerIndex = typeof nowPlayingOverride?.mixerPlaylistIndex === 'number'
                  ? nowPlayingOverride.mixerPlaylistIndex
                  : currentPlayoutIndex;
                // Runtime tracks contain Rekordbox cue points, cold-start intro
                // skips and the unshifted beatmix transition marker.
                const markerEffectiveEnd = getPlayoutDuration(timingItem);
                const effectiveEnd = liveProgressTiming?.effectiveDurationSec ?? markerEffectiveEnd;
                const timeRemaining = liveProgressTiming?.transitionRemainingSec
                  ?? Math.max(0, effectiveEnd - elapsedTime);
                const timeToIntro = liveProgressTiming?.introRemainingSec
                  ?? (intro > 0 ? Math.max(0, intro - elapsedTime) : null);
                const isBeforeIntro = intro > 0 && timeToIntro > 0;
                const categoryColor = currentItem?.categoryColor || timingItem.categoryColor || '#4caf50';
                
                // Create a lighter version for the border
                const borderColor = categoryColor;
                
                return (
                  <Box
                    sx={(theme) => ({
                      bgcolor: theme.palette.mode === 'light'
                        ? hexToRgba(categoryColor, 0.16)
                        : categoryColor,
                      borderRadius: 2,
                      p: 1.75,
                      mb: 1.5,
                      border: `2px solid ${borderColor}`,
                      flexShrink: 0,
                    })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                      {currentPlayoutIndex !== null && (
                        <>
                          <Box
                            onClick={() => toggleChain(currentMixerIndex)}
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              bgcolor: stopAfterTracks.has(currentMixerIndex) ? '#f44336' : '#4caf50',
                              cursor: 'pointer',
                              border: '2px solid rgba(255,255,255,0.5)',
                              transition: 'all 0.2s',
                              flexShrink: 0,
                              '&:hover': {
                                transform: 'scale(1.15)',
                                boxShadow: stopAfterTracks.has(currentMixerIndex)
                                  ? '0 0 10px rgba(244,67,54,0.7)'
                                  : '0 0 10px rgba(76,175,80,0.7)'
                              }
                            }}
                            title={stopAfterTracks.has(currentMixerIndex) ? 'STOP after this item — click to auto-advance' : 'Auto-advance after this item — click to STOP'}
                          />
                          <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.9, fontSize: '0.7rem', letterSpacing: '0.1em', fontWeight: 600 }}>
                            NOW PLAYING {stopAfterTracks.has(currentMixerIndex) ? '— STOPPING AFTER' : ''}
                          </Typography>
                        </>
                      )}
                      {currentPlayoutIndex === null && (
                        <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.9, fontSize: '0.7rem', letterSpacing: '0.1em', fontWeight: 600 }}>
                          NOW PLAYING
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" sx={{ color: 'var(--rs-text)', fontWeight: 'bold', mb: 0.25, lineHeight: 1.25 }} noWrap>
                            {displayTitle}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'var(--rs-text)', opacity: 0.9, mb: 0.25 }} noWrap>
                            {displayArtist}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.8 }}>
                            Started: {isOnAir && playStartTime
                              ? formatClockTime(playStartTime)
                              : (currentItem?.suggestedPlayTime || timingItem.suggestedPlayTime || '—')}
                          </Typography>
                        </Box>
                        
                        {/* Time Information - Moved to left */}
                        <Box sx={{ textAlign: 'left', minWidth: 120, flexShrink: 0 }}>
                          {isOnAir && playStartTime ? (
                            <>
                              {isBeforeIntro && timeToIntro !== null ? (
                                <>
                                  <Typography variant="h4" sx={{ color: 'var(--rs-live-warning)', fontWeight: 'bold', mb: 0.25, lineHeight: 1.1 }}>
                                    {formatDuration(timeToIntro)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.9, display: 'block' }}>
                                    Until Intro Ends
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.8 }}>
                                    {formatDuration(effectiveEnd)} total
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Typography variant="h4" sx={{ color: 'var(--rs-text)', fontWeight: 'bold', mb: 0.25, lineHeight: 1.1 }}>
                                    {formatDuration(timeRemaining)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.9, display: 'block' }}>
                                    Time Remaining
                                  </Typography>
                                  {intro > 0 && (
                                    <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.8 }}>
                                      Intro: {formatDuration(intro)} | {formatDuration(effectiveEnd)} total
                                    </Typography>
                                  )}
                                  {intro === 0 && (
                                    <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.8 }}>
                                      {formatDuration(effectiveEnd)} total
                                    </Typography>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <Typography variant="h4" sx={{ color: 'var(--rs-text)', fontWeight: 'bold', mb: 0.25, lineHeight: 1.1 }}>
                                {formatDuration(effectiveEnd)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.9 }}>
                                Duration
                              </Typography>
                              {intro > 0 && (
                                <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.8, display: 'block', mt: 0.25 }}>
                                  Intro: {formatDuration(intro)}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                      
                      {/* Play Next Button - Right side, bigger */}
                      {(() => {
                        // Find the next item
                        let currentGroupIndex = -1;
                        for (let i = 0; i < groupCommercialBreaks.length; i++) {
                          const entry = groupCommercialBreaks[i];
                          if (entry.type === 'commercial_break_group') {
                            if (entry.items.some(item => item.originalIndex === currentPlayoutIndex)) {
                              currentGroupIndex = i;
                              break;
                            }
                          } else if (entry.originalIndex === currentPlayoutIndex) {
                            currentGroupIndex = i;
                            break;
                          }
                        }
                        
                        const nextGroupIndex = currentGroupIndex + 1;
                        const nextEntry = nextGroupIndex < groupCommercialBreaks.length ? groupCommercialBreaks[nextGroupIndex] : null;
                        
                        if (!nextEntry) return null;
                        
                        // Contrast against the NOW PLAYING fill: light mode uses a pale
                        // wash of the category colour, so ink must stay dark there.
                        const getButtonColor = (bgColor, isLight) => {
                          if (isLight) return '#0f172a';
                          if (!bgColor) return '#ffffff';
                          
                          // Convert hex to RGB - handle both 3 and 6 digit hex
                          let hex = bgColor.replace('#', '');
                          if (hex.length === 3) {
                            hex = hex.split('').map(char => char + char).join('');
                          }
                          
                          if (hex.length !== 6) return '#ffffff'; // Fallback if invalid
                          
                          const r = parseInt(hex.substring(0, 2), 16);
                          const g = parseInt(hex.substring(2, 4), 16);
                          const b = parseInt(hex.substring(4, 6), 16);
                          
                          // Calculate brightness (0-255)
                          const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                          
                          // Use dark color for light backgrounds, light color for dark backgrounds
                          return brightness > 128 ? '#0f172a' : '#ffffff';
                        };
                        
                          const handlePlayNext = (e) => {
                            e?.preventDefault?.();
                            e?.stopPropagation?.();
                            if (!socketRef.current) {
                              setStatusMessage('Transport unavailable');
                              return;
                            }
                            transport('next');
                            setStatusMessage('Playing next item...');
                          };
                        
                        return (
                          <Button
                            type="button"
                            onClick={handlePlayNext}
                            sx={(theme) => {
                              const buttonColor = getButtonColor(categoryColor, theme.palette.mode === 'light');
                              return {
                              bgcolor: 'transparent',
                              border: `2px dashed ${buttonColor}`,
                              borderRadius: 2,
                              minWidth: 112,
                              height: 48,
                              px: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: buttonColor,
                              flexShrink: 0,
                              '&:hover': {
                                bgcolor: `${buttonColor}20`,
                                border: `2px solid ${buttonColor}`,
                                transform: 'scale(1.03)',
                              },
                              transition: 'all 0.3s ease',
                            };
                            }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                border: '2px dashed currentColor',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: 0,
                                  height: 0,
                                  borderLeft: '8px solid currentColor',
                                  borderTop: '5px solid transparent',
                                  borderBottom: '5px solid transparent',
                                  marginLeft: '2px'
                                }}
                              />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Play Next
                            </Typography>
                          </Button>
                        );
                      })()}
                    </Box>
                  </Box>
                );
                }
                // Show placeholder when nothing is playing
                return (
                  <Box
                    sx={{
                      bgcolor: 'var(--rs-border)',
                      borderRadius: 2,
                      p: 1.75,
                      mb: 1.5,
                      border: '2px solid var(--rs-muted)',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'var(--rs-text)', opacity: 0.9, fontSize: '0.7rem', letterSpacing: '0.1em', fontWeight: 600, mb: 1, display: 'block' }}>
                      NOW PLAYING
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rs-muted)', textAlign: 'center', py: 1 }}>
                      No item currently playing
                    </Typography>
                  </Box>
                );
              })()}

              {/* Next Items Preview */}
              {currentPlayoutIndex !== null && currentPlayoutIndex >= 0 && groupCommercialBreaks.length > 0 && (() => {
                // Find the current item in grouped breaks
                let currentGroupIndex = -1;
                for (let i = 0; i < groupCommercialBreaks.length; i++) {
                  const entry = groupCommercialBreaks[i];
                  if (entry.type === 'commercial_break_group') {
                        if (entry.items.some(item => item.originalIndex === currentPlayoutIndex)) {
                          currentGroupIndex = i;
                          break;
                        }
                      } else if (entry.originalIndex === currentPlayoutIndex) {
                        currentGroupIndex = i;
                        break;
                      }
                }
                
                const nextGroupIndex = currentGroupIndex + 1;
                const afterNextGroupIndex = currentGroupIndex + 2;
                const nextEntry = nextGroupIndex < groupCommercialBreaks.length ? groupCommercialBreaks[nextGroupIndex] : null;
                const afterNextEntry = afterNextGroupIndex < groupCommercialBreaks.length ? groupCommercialBreaks[afterNextGroupIndex] : null;
                
                if (!nextEntry && !afterNextEntry) return null;
                
                const renderNextItem = (entry, label) => {
                  if (!entry) return null;
                  
                  // Get the index for selection - use groupStartIndex for commercial breaks, originalIndex for normal items
                  const itemIndex = entry.type === 'commercial_break_group' 
                    ? entry.groupStartIndex 
                    : entry.originalIndex;
                  const isSelected = selectedItemIndex === itemIndex;
                  const liveTiming = liveTimingProjection.get(itemIndex);
                  const displayedStartTime = liveTiming
                    ? formatClockTime(new Date(liveTiming.startTimeMs))
                    : entry.suggestedPlayTime;
                  
                  const lastIndex = entry.type === 'commercial_break_group'
                    ? entry.groupStartIndex + (entry.items?.length || 1) - 1
                    : entry.originalIndex;

                  // Commercial break group
                  if (entry.type === 'commercial_break_group') {
                    return (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedItemIndex(itemIndex);
                        }}
                        sx={{
                          bgcolor: isSelected ? 'rgba(0, 212, 255, 0.2)' : 'var(--rs-paper)',
                          border: `2px solid ${isSelected ? 'var(--rs-accent)' : '#ff9800'}`,
                          borderRadius: 1,
                          p: 1.25,
                          mb: 0.75,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: isSelected ? 'rgba(0, 212, 255, 0.3)' : 'rgba(255, 152, 0, 0.1)',
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Box
                          onClick={(e) => { e.stopPropagation(); toggleChain(lastIndex); }}
                          sx={{
                            width: 18, height: 18, borderRadius: '50%',
                            bgcolor: stopAfterTracks.has(lastIndex) ? '#f44336' : '#4caf50',
                            cursor: 'pointer', border: '2px solid rgba(255,255,255,0.5)',
                            transition: 'all 0.2s', flexShrink: 0, mr: 1.25,
                            '&:hover': {
                              transform: 'scale(1.15)',
                              boxShadow: stopAfterTracks.has(lastIndex)
                                ? '0 0 10px rgba(244,67,54,0.7)'
                                : '0 0 10px rgba(76,175,80,0.7)'
                            }
                          }}
                          title={stopAfterTracks.has(lastIndex) ? 'STOP after this break — click to auto-advance' : 'Auto-advance after this break — click to STOP'}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: 'var(--rs-accent)', fontWeight: 600, letterSpacing: '0.06em' }}>
                            {label}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ color: 'var(--rs-text)', fontWeight: 'bold', lineHeight: 1.25 }} noWrap>
                            Commercial Break
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'var(--rs-muted)', display: 'block' }}>
                            {entry.spotCount} spot{entry.spotCount !== 1 ? 's' : ''} • {displayedStartTime} • {formatDuration(entry.totalDuration || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }
                  
                  // Normal item
                  const runtimeEntry = liveTiming?.track ? { ...entry, ...liveTiming.track } : entry;
                  const displayedDuration = liveTiming?.duration ?? getPlayoutDuration(runtimeEntry);
                  return (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedItemIndex(itemIndex);
                      }}
                      sx={{
                        bgcolor: isSelected
                          ? 'rgba(34,211,238,0.12)'
                          : 'var(--rs-row)',
                        border: `1px solid ${isSelected ? 'rgba(34,211,238,0.55)' : (entry.categoryColor ? hexToRgba(entry.categoryColor, 0.45) : 'rgba(148,163,184,0.16)')}`,
                        borderRadius: 2,
                        p: 1.25,
                        mb: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: isSelected 
                            ? hexToRgba('#22d3ee', 0.3)
                            : hexToRgba(entry.categoryColor || 'var(--rs-border)', 0.1),
                          transform: 'translateX(4px)'
                        }
                      }}
                    >
                      <Box
                        onClick={(e) => { e.stopPropagation(); toggleChain(lastIndex); }}
                        sx={{
                          width: 18, height: 18, borderRadius: '50%',
                          bgcolor: stopAfterTracks.has(lastIndex) ? '#f44336' : '#4caf50',
                          cursor: 'pointer', border: '2px solid rgba(255,255,255,0.5)',
                          transition: 'all 0.2s', flexShrink: 0, mr: 1.25,
                          '&:hover': {
                            transform: 'scale(1.15)',
                            boxShadow: stopAfterTracks.has(lastIndex)
                              ? '0 0 10px rgba(244,67,54,0.7)'
                              : '0 0 10px rgba(76,175,80,0.7)'
                          }
                        }}
                        title={stopAfterTracks.has(lastIndex) ? 'STOP after this item — click to auto-advance' : 'Auto-advance after this item — click to STOP'}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: 'var(--rs-accent)', fontWeight: 600, letterSpacing: '0.06em' }}>
                          {label}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: 'var(--rs-text)', fontWeight: 'bold', lineHeight: 1.25 }} noWrap>
                          {runtimeEntry.title || 'Untitled'}
                        </Typography>
                          <Typography variant="caption" sx={{ color: 'var(--rs-muted)', display: 'block' }} noWrap>
                          {runtimeEntry.artist || '-'} • {displayedStartTime} • {formatDuration(displayedDuration)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                };
                
                // Calculate insert index - after the "After Next" item if it exists, otherwise after "Next"
                let insertAfterIndex = null;
                if (afterNextEntry) {
                  insertAfterIndex = afterNextEntry.type === 'commercial_break_group'
                    ? afterNextEntry.groupStartIndex + (afterNextEntry.items?.length || 0)
                    : afterNextEntry.originalIndex + 1;
                } else if (nextEntry) {
                  insertAfterIndex = nextEntry.type === 'commercial_break_group'
                    ? nextEntry.groupStartIndex + (nextEntry.items?.length || 0)
                    : nextEntry.originalIndex + 1;
                }
                
                return (
                  <Box sx={{ mb: 1.25, flexShrink: 0 }}>
                    {renderNextItem(nextEntry, 'Next')}
                    {renderNextItem(afterNextEntry, 'After Next')}
                    {insertAfterIndex !== null && (
                      <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Add />}
                          onClick={() => {
                            setInsertAtIndex(insertAfterIndex);
                            setLibraryDialogOpen(true);
                          }}
                          disabled={selectedItemIndex === null}
                          sx={{
                            bgcolor: selectedItemIndex !== null ? 'var(--rs-accent)' : 'var(--rs-border)',
                            color: selectedItemIndex !== null ? 'var(--rs-paper)' : 'var(--rs-muted)',
                            '&:hover': selectedItemIndex !== null ? { filter: 'brightness(0.92)' } : {},
                            '&.Mui-disabled': {
                              bgcolor: 'var(--rs-border)',
                              color: 'var(--rs-muted)',
                            }
                          }}
                        >
                          Add Item
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Upcoming Items */}
              {groupCommercialBreaks.length > 0 && (
                <TableContainer
                  sx={{
                    flex: '1 0 180px',
                    minHeight: 180,
                    borderRadius: 2,
                    border: '1px solid rgba(148,163,184,0.16)',
                    bgcolor: 'var(--rs-overlay-soft)',
                    overflow: 'auto',
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      '& .MuiTableCell-root': {
                        borderColor: 'transparent',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...logHeadCellSx, width: 44, px: 1 }}></TableCell>
                        <TableCell sx={{ ...logHeadCellSx, width: 120 }}>Play Time</TableCell>
                        <TableCell sx={{ ...logHeadCellSx, width: 40, px: 0.5 }}></TableCell>
                        <TableCell sx={logHeadCellSx}>Title</TableCell>
                        <TableCell sx={logHeadCellSx}>Artist</TableCell>
                        <TableCell sx={{ ...logHeadCellSx, width: 88 }}>Duration</TableCell>
                        <TableCell sx={{ ...logHeadCellSx, width: 148 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            Actions
                            <IconButton
                              size="small"
                              onClick={handleAddItemAfterSelected}
                              disabled={selectedItemIndex === null}
                              sx={{
                                color: selectedItemIndex !== null ? 'var(--rs-accent)' : 'var(--rs-muted)',
                                border: '1px solid',
                                borderColor: selectedItemIndex !== null ? 'rgba(34,211,238,0.35)' : 'rgba(71,85,105,0.35)',
                                borderRadius: 1,
                                width: 26,
                                height: 26,
                                '&:hover': selectedItemIndex !== null ? { bgcolor: 'rgba(34,211,238,0.12)' } : {},
                                '&.Mui-disabled': { color: '#475569' },
                              }}
                              title={selectedItemIndex !== null ? 'Add item after selected' : 'Select an item first'}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupCommercialBreaks
                        .filter((entry) => {
                          // Filter out played items and the "Next" / "After Next"
                          // entries shown in the enlarged preview above.
                          if (currentPlayoutIndex === null) return true; // Show all if nothing is playing
                          if (entry.type === 'commercial_break_group') {
                            // Only show commercial break if it has at least one item beyond the preview
                            return entry.items.some(item => item.originalIndex >= firstTableIndex);
                          }
                          return entry.originalIndex >= firstTableIndex;
                        })
                        .map((entry, entryIndex, filteredEntries) => {
                          // Check if this is the first next-hour item (need to show divider before it)
                          const isFirstNextHour = entry.isNextHour && 
                            (entryIndex === 0 || !filteredEntries[entryIndex - 1]?.isNextHour);
                          
                          // Calculate next hour info for the divider
                          const nextHour = (selectedHour + 1) % 24;
                          const nextDate = nextHour === 0 ? addOneDay(selectedDate) : selectedDate;
                          const nextHourFormatted = `${String(nextHour).padStart(2, '0')}:00`;
                          
                          const rows = [];
                          
                          // Insert divider row before first next-hour item
                          if (isFirstNextHour) {
                            rows.push(
                              <TableRow
                                key={`next-hour-divider-${entryIndex}`}
                                sx={{
                                  bgcolor: 'rgba(0, 212, 255, 0.1)',
                                  borderTop: '2px solid var(--rs-accent)',
                                  borderBottom: '2px solid var(--rs-accent)',
                                  '& td': { py: 1.5 }
                                }}
                              >
                                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <Box sx={{ flex: 1, height: 1, bgcolor: 'var(--rs-accent)', opacity: 0.3 }} />
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'var(--rs-accent)', 
                                        fontWeight: 600, 
                                        px: 2,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      Next Hour: {nextDate} {nextHourFormatted}
                                    </Typography>
                                    <Box sx={{ flex: 1, height: 1, bgcolor: 'var(--rs-accent)', opacity: 0.3 }} />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          }
                          
                          // Original entry rendering
                        // Commercial break group
                        if (entry.type === 'commercial_break_group') {
                          const isExpanded = expandedBreaks.has(entry.groupStartIndex);
                          const entryRows = [];
                          
                          // The last item index in the break — chain circle applies to this
                          const lastBreakItemIndex = entry.items[entry.items.length - 1]?.originalIndex;

                          // Summary row
                          entryRows.push(
                            <TableRow
                              key={`break-group-${entry.groupStartIndex}`}
                              draggable
                              onDragStart={(e) => {
                                e.stopPropagation();
                                handleDragStart(e, entry.groupStartIndex);
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDragOver(e, entry.groupStartIndex);
                              }}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => {
                                e.stopPropagation();
                                handleDrop(e, entry.groupStartIndex);
                              }}
                              onClick={(e) => {
                                // Only toggle if not clicking on chain circle
                                if (!e.target.closest('[data-chain-circle]')) {
                                  toggleBreakExpanded(entry.groupStartIndex);
                                  setSelectedItemIndex(entry.groupStartIndex);
                                }
                              }}
                              onDoubleClick={() => handleItemClick(entry.groupStartIndex)}
                              sx={{
                                bgcolor: selectedItemIndex === entry.groupStartIndex
                                  ? 'rgba(34,211,238,0.14)'
                                  : 'rgba(245,158,11,0.08)',
                                borderLeft: `3px solid ${selectedItemIndex === entry.groupStartIndex ? 'var(--rs-accent)' : '#f59e0b'}`,
                                opacity: draggedIndex === entry.groupStartIndex ? 0.5 : 1,
                                boxShadow: dragOverIndex === entry.groupStartIndex
                                  ? 'inset 0 2px 0 var(--rs-accent)'
                                  : 'none',
                                transition: 'background-color 120ms ease',
                                '&:hover': {
                                  bgcolor: selectedItemIndex === entry.groupStartIndex
                                    ? 'rgba(34,211,238,0.2)'
                                    : 'rgba(245,158,11,0.14)',
                                  cursor: 'pointer',
                                },
                              }}
                            >
                              <TableCell sx={{ ...logBodyCellSx, width: 44, px: 1, textAlign: 'center' }}>
                                {lastBreakItemIndex !== undefined && (
                                  <Box
                                    data-chain-circle
                                    onClick={(e) => { e.stopPropagation(); toggleChain(lastBreakItemIndex); }}
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: '50%',
                                      bgcolor: stopAfterTracks.has(lastBreakItemIndex) ? '#f44336' : '#4caf50',
                                      cursor: 'pointer',
                                      mx: 'auto',
                                      border: '1px solid rgba(255,255,255,0.25)',
                                      transition: 'background-color 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.2)',
                                        boxShadow: stopAfterTracks.has(lastBreakItemIndex)
                                          ? '0 0 8px rgba(244,67,54,0.6)'
                                          : '0 0 8px rgba(76,175,80,0.6)'
                                      }
                                    }}
                                    title={stopAfterTracks.has(lastBreakItemIndex) ? 'Chain STOP — click to auto-advance' : 'Auto-advance — click to stop after break'}
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={{ ...logBodyCellSx, color: 'var(--rs-accent)', fontWeight: 600, width: 120, fontVariantNumeric: 'tabular-nums' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {isExpanded ? <ExpandLess sx={{ color: 'var(--rs-live-commercial-fg)' }} /> : <ExpandMore sx={{ color: 'var(--rs-live-commercial-fg)' }} />}
                                  {entry.suggestedPlayTime}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ ...logBodyCellSx, width: 40, px: 0.5 }} />
                              <TableCell sx={logBodyCellSx}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label="COMMERCIAL"
                                    size="small"
                                    sx={{
                                      bgcolor: 'var(--rs-live-commercial-bg)',
                                      color: 'var(--rs-live-commercial-fg)',
                                      border: '1px solid var(--rs-live-commercial-fg)',
                                      fontWeight: 700,
                                      fontSize: '0.65rem',
                                      height: 22,
                                    }}
                                  />
                                  <Typography component="span" sx={{ fontWeight: 600, color: 'var(--rs-text)' }}>
                                    Commercial Break ({entry.spotCount} spot{entry.spotCount !== 1 ? 's' : ''})
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ ...logBodyCellSx, color: 'var(--rs-muted)' }}>—</TableCell>
                              <TableCell sx={{ ...logBodyCellSx, color: 'var(--rs-muted)', fontVariantNumeric: 'tabular-nums' }}>
                                {formatDuration(entry.totalDuration || 0)}
                              </TableCell>
                              <TableCell sx={{ ...logBodyCellSx, width: 148 }} />
                            </TableRow>
                          );
                          
                          // Individual ad spots when expanded
                          if (isExpanded) {
                            entry.items
                              .filter((adItem) => {
                                // Only show ad spots that are after the Next/After Next preview (same as table filter)
                                if (currentPlayoutIndex === null) return true;
                                return adItem.originalIndex >= firstTableIndex;
                              })
                              .forEach((adItem, adIdx) => {
                              const adCategoryName = adItem.categoryName || getCategoryInfo(adItem).categoryName;
                              const adCategoryColor = adItem.categoryColor || getCategoryInfo(adItem).categoryColor;
                              
                              entryRows.push(
                                <TableRow
                                  key={`break-ad-${adItem.originalIndex}`}
                                  onClick={(e) => {
                                    // Don't select if clicking on chain circle or action buttons
                                    if (!e.target.closest('[data-chain-circle]') && 
                                        !e.target.closest('button') && 
                                        !e.target.closest('.MuiIconButton-root')) {
                                      setSelectedItemIndex(adItem.originalIndex);
                                    }
                                  }}
                                  onDoubleClick={() => handleItemClick(adItem.originalIndex)}
                                  sx={{
                                    bgcolor: selectedItemIndex === adItem.originalIndex
                                      ? 'rgba(34,211,238,0.12)'
                                      : 'var(--rs-row)',
                                    borderLeft: `3px solid ${selectedItemIndex === adItem.originalIndex ? 'var(--rs-accent)' : adCategoryColor}`,
                                    cursor: 'pointer',
                                    transition: 'background-color 120ms ease',
                                    '&:hover': {
                                      bgcolor: selectedItemIndex === adItem.originalIndex
                                        ? 'rgba(34,211,238,0.18)'
                                        : 'rgba(34,211,238,0.06)',
                                    },
                                  }}
                                >
                                  <TableCell sx={{ ...logBodyCellSx, width: 44, px: 1, textAlign: 'center' }}>
                                    <Box
                                      onClick={(e) => { e.stopPropagation(); toggleChain(adItem.originalIndex); }}
                                      sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        bgcolor: stopAfterTracks.has(adItem.originalIndex) ? '#f44336' : '#4caf50',
                                        cursor: 'pointer',
                                        mx: 'auto',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        transition: 'background-color 0.2s',
                                        '&:hover': { transform: 'scale(1.2)' }
                                      }}
                                      title={stopAfterTracks.has(adItem.originalIndex) ? 'Chain STOP' : 'Auto-advance'}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ ...logBodyCellSx, pl: 5, color: 'var(--rs-accent)', fontWeight: 600, width: 120, fontVariantNumeric: 'tabular-nums' }}>
                                    {adItem.suggestedPlayTime}
                                  </TableCell>
                                  <TableCell sx={{ ...logBodyCellSx, width: 40, px: 0.5 }}>
                                    {hasSystemFeature('ai_insights') && adItem.category?.ai_insights_enabled && (
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); setAiInsightsItem(adItem); setAiInsightsOpen(true); }}
                                        title="AI Insights"
                                        sx={{ color: 'var(--rs-accent)' }}
                                      >
                                        <Psychology fontSize="small" />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ ...logBodyCellSx, fontSize: '0.85rem' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip
                                        label={adCategoryName}
                                        size="small"
                                        sx={{
                                          bgcolor: hexToRgba(adCategoryColor, 0.22),
                                          color: 'var(--rs-text)',
                                          border: `1px solid ${hexToRgba(adCategoryColor, 0.45)}`,
                                          fontSize: '0.65rem',
                                          height: 20,
                                          fontWeight: 600,
                                        }}
                                      />
                                      <Typography component="span" sx={{ color: 'var(--rs-text)' }}>
                                        {adItem.title || 'Spot'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ ...logBodyCellSx, fontSize: '0.85rem', color: 'var(--rs-muted)' }}>
                                    {adItem.artist || '—'}
                                  </TableCell>
                                  <TableCell sx={{ ...logBodyCellSx, fontSize: '0.85rem', color: 'var(--rs-muted)', fontVariantNumeric: 'tabular-nums' }}>
                                    {formatDuration(adItem.duration || 0)}
                                  </TableCell>
                                  <TableCell sx={{ ...logBodyCellSx, width: 148 }}>
                                    <Box sx={logActionGroupSx}>
                                      <IconButton size="small" onClick={() => moveItem(adItem.originalIndex, 'up')} disabled={adItem.originalIndex === 0} sx={{ color: 'var(--rs-muted)' }}>
                                        <ArrowUpward fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => moveItem(adItem.originalIndex, 'down')} disabled={adItem.originalIndex === playlistItems.length - 1} sx={{ color: 'var(--rs-muted)' }}>
                                        <ArrowDownward fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => removeItem(adItem.originalIndex)} sx={{ color: '#f87171' }}>
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            });
                          }
                          
                          // Combine divider (if needed) with entry rows
                          return [...rows, ...entryRows];
                        }
                        
                        // Normal item
                        const item = entry;
                        const index = entry.originalIndex;
                        const isCurrentlyPlaying = currentPlayoutIndex === index;
                        const categoryName = item.categoryName || getCategoryInfo(item).categoryName;
                        const categoryColor = item.categoryColor || getCategoryInfo(item).categoryColor;
                        
                        // Skip the currently playing item in the table (it's shown above)
                        if (isCurrentlyPlaying) {
                          // Still return divider if needed
                          return rows.length > 0 ? rows : null;
                        }
                        
                        const normalRow = (
                          <TableRow
                            key={`${item.title}-${index}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onClick={(e) => {
                              // Don't select if clicking on chain circle or action buttons
                              if (!e.target.closest('[data-chain-circle]') && 
                                  !e.target.closest('button') && 
                                  !e.target.closest('.MuiIconButton-root')) {
                                setSelectedItemIndex(index);
                              }
                            }}
                            onDoubleClick={() => handleItemClick(index)}
                            sx={{
                              bgcolor: selectedItemIndex === index
                                ? 'rgba(34,211,238,0.14)'
                                : 'var(--rs-row)',
                              borderLeft: `3px solid ${selectedItemIndex === index ? 'var(--rs-accent)' : categoryColor}`,
                              cursor: 'pointer',
                              opacity: draggedIndex === index ? 0.5 : 1,
                              boxShadow: dragOverIndex === index ? 'inset 0 2px 0 var(--rs-accent)' : 'none',
                              transition: 'background-color 120ms ease',
                              '&:hover': {
                                bgcolor: selectedItemIndex === index
                                  ? 'rgba(34,211,238,0.2)'
                                  : 'rgba(34,211,238,0.07)',
                              },
                            }}
                          >
                            <TableCell sx={{ ...logBodyCellSx, width: 44, px: 1, textAlign: 'center' }}>
                              <Box
                                data-chain-circle
                                onClick={(e) => { e.stopPropagation(); toggleChain(index); }}
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  bgcolor: stopAfterTracks.has(index) ? '#f44336' : '#4caf50',
                                  cursor: 'pointer',
                                  mx: 'auto',
                                  border: '1px solid rgba(255,255,255,0.25)',
                                  transition: 'background-color 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.2)',
                                    boxShadow: stopAfterTracks.has(index)
                                      ? '0 0 8px rgba(244,67,54,0.6)'
                                      : '0 0 8px rgba(76,175,80,0.6)'
                                  }
                                }}
                                title={stopAfterTracks.has(index) ? 'Chain STOP — click to auto-advance' : 'Auto-advance — click to stop after this item'}
                              />
                            </TableCell>
                            <TableCell sx={{ ...logBodyCellSx, color: 'var(--rs-accent)', fontWeight: 600, width: 120, fontVariantNumeric: 'tabular-nums' }}>
                              {item.suggestedPlayTime}
                            </TableCell>
                            <TableCell sx={{ ...logBodyCellSx, width: 40, px: 0.5 }}>
                              {hasSystemFeature('ai_insights') && item.category?.ai_insights_enabled && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setAiInsightsItem(item); setAiInsightsOpen(true); }}
                                  title="AI Insights"
                                  sx={{ color: 'var(--rs-accent)' }}
                                >
                                  <Psychology fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell sx={logBodyCellSx}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                <Chip
                                  label={categoryName}
                                  size="small"
                                  sx={{
                                    bgcolor: hexToRgba(categoryColor, 0.22),
                                    color: 'var(--rs-text)',
                                    border: `1px solid ${hexToRgba(categoryColor, 0.5)}`,
                                    fontSize: '0.65rem',
                                    height: 22,
                                    fontWeight: 600,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography noWrap sx={{ fontWeight: 600, color: 'var(--rs-text)' }}>
                                  {item.title || 'Untitled'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...logBodyCellSx, color: 'var(--rs-muted)' }}>
                              {item.artist || '—'}
                            </TableCell>
                            <TableCell sx={{ ...logBodyCellSx, color: 'var(--rs-muted)', fontVariantNumeric: 'tabular-nums' }}>
                              {formatDuration(item.duration || 0)}
                            </TableCell>
                            <TableCell sx={{ ...logBodyCellSx, width: 148 }}>
                              <Box sx={logActionGroupSx}>
                                <IconButton size="small" onClick={() => moveItem(index, 'up')} disabled={index === 0} sx={{ color: 'var(--rs-muted)' }}>
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => moveItem(index, 'down')} disabled={index === playlistItems.length - 1} sx={{ color: 'var(--rs-muted)' }}>
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => removeItem(index)} sx={{ color: '#f87171' }}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                        
                        // Combine divider (if needed) with normal row
                        return [...rows, normalRow];
                      })
                      .flat()}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
                </Card>
              );
            }}
          />
        )}
      </RoomConsole>

      <Dialog
        open={createCartwallDialogOpen}
        onClose={() => { setCreateCartwallDialogOpen(false); setNewCartwallName(''); setCreateCartwallError(''); }}
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: 'var(--rs-accent)' }}>Create Cartwall</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {createCartwallError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateCartwallError('')}>
              {createCartwallError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Cartwall name"
            value={newCartwallName}
            onChange={(e) => { setNewCartwallName(e.target.value); setCreateCartwallError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && createCartwall()}
            sx={{ input: { color: 'var(--rs-text)' } }}
          />
          {stations.length <= 1 && !selectedStation && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              No station available. Add a station in Settings.
            </Typography>
          )}
        </DialogContent>
        <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => { setCreateCartwallDialogOpen(false); setNewCartwallName(''); setCreateCartwallError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={createCartwall} disabled={!newCartwallName.trim() || createCartwallLoading}>
            {createCartwallLoading ? 'Creating...' : 'Create'}
          </Button>
        </Box>
      </Dialog>

      {/* Library Selection Dialog */}
      <Dialog
        open={libraryDialogOpen}
        onClose={() => {
          setLibraryDialogOpen(false);
          setInsertAtIndex(null);
          setSelectedCategory('');
          setCartSearch('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' }
        }}
      >
        <DialogTitle sx={{ color: 'var(--rs-accent)', borderBottom: '1px solid var(--rs-border)' }}>
          Add Item from Library
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Search library"
              value={cartSearch}
              onChange={(e) => setCartSearch(e.target.value)}
              sx={{ input: { color: 'var(--rs-text)' } }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
                sx={{ color: 'var(--rs-text)' }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={1} sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {allAssets
              .filter((asset) => {
                // Text search filter
                const matchesSearch = !cartSearch ||
                  (asset.title || '').toLowerCase().includes(cartSearch.toLowerCase()) ||
                  (asset.artist || '').toLowerCase().includes(cartSearch.toLowerCase());
                
                // Category filter
                const matchesCategory = !selectedCategory ||
                  (asset.categories || []).some(cat => cat.id === parseInt(selectedCategory)) ||
                  asset.categoryId === parseInt(selectedCategory);
                
                return matchesSearch && matchesCategory;
              })
              .map((asset) => (
                <Grid item xs={12} sm={6} md={4} key={asset.id}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      justifyContent: 'flex-start',
                      borderColor: 'var(--rs-accent)',
                      color: 'var(--rs-accent)',
                      '&:hover': { borderColor: 'var(--rs-accent)', bgcolor: 'var(--rs-surface)' }
                    }}
                    onClick={() => handleAddFromLibrary(asset)}
                  >
                    <Box sx={{ textAlign: 'left', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {asset.title}
                        </Typography>
                        {(asset.categories || []).slice(0, 2).map((cat) => (
                          <Chip
                            key={cat.id}
                            label={cat.name}
                            size="small"
                            sx={{
                              bgcolor: cat.color || '#666',
                              color: 'var(--rs-text)',
                              fontSize: '0.65rem',
                              height: 18
                            }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
                        {asset.artist || 'Unknown'} • {formatDuration(asset.duration || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 0.25 }} noWrap>
                        Last: {formatDateTime(asset.last_played_at)} • Next: {asset.next_scheduled ? formatDateTime(asset.next_scheduled.scheduled_time) : '—'}
                        {stations.length > 1 && asset.next_scheduled?.station_name && ` • ${asset.next_scheduled.station_name}`}
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
          </Grid>
        </DialogContent>
      </Dialog>

      <AiInsightsDialog
        open={aiInsightsOpen}
        onClose={() => { setAiInsightsOpen(false); setAiInsightsItem(null); }}
        item={aiInsightsItem}
        stationId={selectedStation || null}
      />
    </Box>
  );
}
